import * as T from 'three';
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js';
import {layout as L} from './layout.js';
import {ARRIVAL_DURATIONS as DURATIONS} from './state.js';
import {itemName} from './locales.js';
import {lineClear,setDoorAngle,canOccupy} from './navigation.js';
import {createInteriorFinishes} from './interior-finishes.js';
import {createTwoBoneSolver} from './mentor-arm-motion.js';

const RIDING=new Set(['drive','turn','park','van-door']);
const OUTSIDE_BRIEF=new Set(['exit','recall','brief']);
const CINEMATIC=new Set([...RIDING,...OUTSIDE_BRIEF]);
const TRAVEL_ANIMATION=new Set(['drive','turn','park','exit']);

// Local illustration: optional preloaded CC0 trees; no runtime CDN or physics.
// The no-argument path remains synchronous for tools and baseline tests.
export function buildMissionWorld({trees=null,interior=null,floorTexture=null}={}){
 // Early evening: a tiny procedural equirectangular sky, no image or runtime network.
 // The lower half stays at the fog colour so the horizon joins the landscape.
 const scene=new T.Scene(),skyPixels=new Uint8Array(256*4),horizon=[201,172,164],zenith=[87,108,144];
 for(let y=0;y<256;y++){
  const t=Math.pow(Math.max(0,(y-128)/127),.72);
  for(let c=0;c<3;c++)skyPixels[y*4+c]=Math.round(T.MathUtils.lerp(horizon[c],zenith[c],t));
  skyPixels[y*4+3]=255;
 }
 const skyTexture=new T.DataTexture(skyPixels,1,256,T.RGBAFormat);skyTexture.colorSpace=T.SRGBColorSpace;skyTexture.mapping=T.EquirectangularReflectionMapping;skyTexture.magFilter=T.LinearFilter;skyTexture.minFilter=T.LinearFilter;skyTexture.needsUpdate=true;
 scene.background=skyTexture;scene.fog=new T.Fog('#c9aca4',18,42);
 const colors={cream:'#efe7d7',white:'#fff9e9',sage:'#7e9987',grass:'#a8b99a',ink:'#344c4b',wood:'#b1835b',sand:'#d1b992',metal:'#9baaa6',amber:'#e8af4f',coral:'#ca785c',road:'#657771',sky:'#b5d3d0',skin:'#d8a27c',hair:'#504035',blue:'#557b89',black:'#2b3939'};
 const materials=Object.fromEntries(Object.entries(colors).map(([k,color])=>[k,new T.MeshLambertMaterial({color})]));
 // A specular glass insert, not an environment-map or a fake rear-view feed.
 materials.mirrorGlass=new T.MeshPhongMaterial({color:'#9eb8b7',specular:'#d9e5de',shininess:85});
 materials.verge=new T.MeshLambertMaterial({color:'#99a891'});
 const unit=new T.BoxGeometry(1,1,1),sphere=new T.SphereGeometry(1,10,7),cylinder=new T.CylinderGeometry(1,1,1,12);
 // A shared gently lobed crown avoids faceted identical balls without per-tree buffers.
 const foliage=new T.SphereGeometry(1,12,8),fp=foliage.attributes.position;
 for(let i=0;i<fp.count;i++){
  const x=fp.getX(i),y=fp.getY(i),z=fp.getZ(i),a=Math.atan2(z,x);
  const r=1+.085*Math.cos(5*a+1.7*y)*(1-y*y)+.04*Math.sin(3*a-4*y)*(1-y*y);
  fp.setXYZ(i,x*r,y,z*r);
 }
 foliage.computeVertexNormals();foliage.computeBoundingSphere();
 const scenery=new T.Group();scenery.name='fixed-worksite';scene.add(scenery);
 function group(parent,name){const g=new T.Group();g.name=name;parent.add(g);return g;}
 function box(p,w,h,d,x=0,y=0,z=0,m='cream',name=''){
  const o=new T.Mesh(unit,materials[m]);o.scale.set(w,h,d);o.position.set(x,y,z);o.name=name;p.add(o);return o;
 }
 function ball(p,x,y,z,sx,sy,sz,m){const o=new T.Mesh(sphere,materials[m]);o.position.set(x,y,z);o.scale.set(sx,sy,sz);p.add(o);return o;}
 function cyl(p,r,h,x,y,z,m='metal'){const o=new T.Mesh(cylinder,materials[m]);o.scale.set(r,h,r);o.position.set(x,y,z);p.add(o);return o;}
 // Merge each material within a local coordinate system, retaining actual object
 // bounds for interaction. Characters/hinges and tools remain independently movable.
 function batch(root){
  root.updateWorldMatrix(true,true);const inverse=root.matrixWorld.clone().invert(),sets=new Map();
  root.traverse(o=>{if(o.isMesh){const g=o.geometry.index?o.geometry.toNonIndexed():o.geometry.clone();g.applyMatrix4(new T.Matrix4().multiplyMatrices(inverse,o.matrixWorld));if(!sets.has(o.material))sets.set(o.material,[]);sets.get(o.material).push(g);}});
  root.clear();
  for(const [mat,parts] of sets){const g=mergeGeometries(parts),m=new T.Mesh(g,mat);m.matrixAutoUpdate=false;root.add(m);parts.forEach(p=>p.dispose());}
 }
 function tree(root,x,z,scale=1,species='CommonTree_5'){
  if(trees){
   const asset=trees[species];if(!asset?.parts?.length)throw new Error(`Missing environment tree ${species}`);
   const rotation=(Math.sin(x*1.37+z*.71)+1)*Math.PI;
   for(const part of asset.parts){const mesh=new T.Mesh(part.geometry,part.material);mesh.position.set(x,0,z);mesh.rotation.y=rotation;mesh.scale.setScalar(scale);root.add(mesh);}
   return;
  }
  // Roots tie the trunk into the ground; two crossing low forms share the wood batch.
  box(root,.6*scale,.10*scale,.12*scale,x,.05*scale,z,'wood');
  box(root,.12*scale,.09*scale,.6*scale,x,.045*scale,z,'wood');
  const trunk=cyl(root,.14*scale,1.8*scale,x,.9*scale,z,'wood');trunk.rotation.z=.035;
  for(const sign of [-1,1]){const branch=cyl(root,.065*scale,.9*scale,x+sign*.22*scale,1.55*scale,z,'wood');branch.rotation.z=sign*-.65;}
  const variation=Math.sin(x*1.37+z*.71),lean=variation*.13;
  for(const [dx,dy,dz,sx,sy,sz,material] of [
   [lean,2.7,0,1.04,1.22+variation*.14,.95,'sage'],
   [-.63,2.15,.17,.78,.86,.73,'sage'],
   [.65,2.32,-.22,.75,.96-variation*.1,.78,'grass'],
  ]){
   const crown=new T.Mesh(foliage,materials[material]);
   crown.position.set(x+dx*scale,dy*scale,z+dz*scale);crown.scale.set(sx*scale,sy*scale,sz*scale);
   crown.rotation.y=variation*.8;root.add(crown);
  }
 }
 // Roof slabs overlap at the ridge and sit directly on the solid gable profile.
 // The gables have real depth and outward faces, not background-coloured fillers.
 function pitchedRoof(root,{x=0,z=0,width,depth,eave,rise,wallThickness=.18,overhang=.35}){
  const slope=rise/(depth/2),angle=Math.atan(slope),thickness=.14;
  const start=-.08,end=depth/2+overhang,run=end-start;
  for(const side of [-1,1]){
   const centre=(start+end)/2,normalOffset=thickness/2;
   const roof=box(root,width+2*overhang,thickness,run/Math.cos(angle),x,eave+rise-slope*centre+normalOffset*Math.cos(angle),z+side*(centre+normalOffset*Math.sin(angle)),'coral');
   roof.rotation.x=side*angle;
  }
  // Ridge flashing covers the intersection, avoiding exposed nearly coincident faces.
  const ridgeBottom=eave+rise+thickness/Math.cos(angle)-.038;
  box(root,width+2*overhang+.02,.10,.20,x,ridgeBottom+.05,z,'coral');
  const shape=new T.Shape();shape.moveTo(-depth/2,0);shape.lineTo(depth/2,0);shape.lineTo(depth/2,.035);shape.lineTo(0,rise+.085);shape.lineTo(-depth/2,.035);shape.closePath();
  const geometry=new T.ExtrudeGeometry(shape,{depth:wallThickness,bevelEnabled:false});
  for(const edge of [-1,1]){
   const gable=new T.Mesh(geometry,materials.cream);gable.rotation.y=Math.PI/2;
   gable.position.set(x+edge*width/2-wallThickness/2,eave-.015,z);root.add(gable);
  }
 }
 function neighbour(root,x,z,yaw=0){
  const home=group(root,'neighbour-house');home.position.set(x,0,z);home.rotation.y=yaw;
  box(home,6,2.8,4.8,0,1.4,0,'cream');
  pitchedRoof(home,{width:6,depth:4.8,eave:2.8,rise:.8,wallThickness:.16,overhang:.25});
  // Foundation is interrupted by the entrance, and gutters feed wall-mounted downpipes.
  for(const [a,b] of [[-3,.15],[1.05,3]])box(home,b-a,.24,.10,(a+b)/2,.12,2.43,'sand');
  box(home,6,.24,.10,0,.12,-2.43,'sand');
  for(const side of [-1,1]){
   box(home,.10,.24,4.8,side*3.03,.12,0,'sand');
   box(home,6.5,.10,.11,0,2.69,side*2.68,'metal');
   box(home,.08,2.56,.08,-2.8,1.36,side*2.44,'metal');
   box(home,.08,.10,.29,-2.8,2.69,side*2.56,'metal');
  }
  box(home,.48,.9,.55,1.6,3.4,-.8,'wood');box(home,.9,2.1,.06,.6,1.05,2.43,'sage');
  box(home,.09,.05,.09,.91,1.1,2.49,'amber');box(home,1.3,.12,.7,.6,.06,2.7,'sand');
  for(const wx of [-1.8,2]){
   box(home,1.15,1.05,.045,wx,1.65,2.43,'sky');
   for(const dx of [-.61,0,.61])box(home,.06,1.17,.08,wx+dx,1.65,2.47,'wood');
   for(const yy of [1.08,2.22])box(home,1.27,.06,.08,wx,yy,2.47,'white');
   box(home,1.4,.12,.32,wx,1.03,2.56,'wood');
  }
  box(home,7.2,.025,1.4,0,.0125,3.4,'sand');
  for(const sx of [-1,1])for(let i=0;i<4;i++){const zz=-1.8+i*1.25;box(home,.07,.7,.07,sx*3.7,.35,zz,'wood');}
  for(const sx of [-1,1])box(home,.06,.06,4,sx*3.7,.5,0,'white');
  for(const xx of [-2.5,-1.6,-.7])ball(home,xx,.4,3.3,.55,.42,.42,'sage');
 }
 // A fixed worksite never follows the dialogue. Only the finite roadside pool
 // scrolls while riding; it freezes in place as the real approach begins.
 const ground=group(scene,'road-ground');
 box(ground,78,.12,280,-6,-.08,100,'grass');
 box(ground,L.route.width,.035,280,0,-.0175,100,'road');
 // Compacted verge sits just below asphalt and above grass; the driveway overlays it.
 for(const side of [-1,1])box(ground,1.1,.012,280,side*2.95,-.014,100,'verge');
 const roadEdge=-L.route.width/2,drivewayEdge=-10.8;
 box(scenery,roadEdge-drivewayEdge,.035,4.8,(roadEdge+drivewayEdge)/2,-.0175,10,'sand');
 box(scenery,2,.04,3.6,-6,-.02,5.8,'sand');
 for(let z=-34;z<-10;z+=3)box(ground,.12,.008,1.25,0,.007,z,'cream');
 box(ground,.16,.08,280,2.5,.04,100,'cream');
 for(const [a,b] of [[-40,7.6],[12.4,240]])box(ground,.16,.08,b-a,-2.5,.04,(a+b)/2,'cream');
 for(let i=0;i<24;i++){const x=i%2?5.4+(i%3)*1.4:-17.5-(i%3)*1.2,z=-19+Math.floor(i/2)*4.1;tree(scenery,x,z,.8+(i%4)*.1,i%4===0?'Pine_5':'CommonTree_5');}
 for(const [x,z,yaw] of [[-24,-16,Math.PI/2],[15,-12,-Math.PI/2],[-24,23,Math.PI/2],[15,24,-Math.PI/2]])neighbour(scenery,x,z,yaw);
 // Near-site details keep both the road and the final on-foot view inhabited.
 for(const x of [-12.5,-3.5]){box(scenery,.12,.9,.12,x,.45,5.7,'wood');box(scenery,.42,.32,.35,x,.94,5.7,'sage');}
 for(let i=0;i<7;i++){const z=-2.8+i*1.2;box(scenery,.09,.85,.09,-14,.425,z,'wood');}
 for(const y of [.3,.65])box(scenery,.06,.06,7.3,-14,y,.7,'white');
 for(const x of [-11.9,-10.7,-9.5]){box(scenery,.55,.22,.42,x,.11,4.8,'coral');ball(scenery,x,.48,4.8,.38,.35,.31,'sage');for(const dx of [-.13,.13])ball(scenery,x+dx,.78,4.8,.06,.06,.06,'amber');}
 const roadside=group(scene,'recycled-roadside'),template=new T.Group();
 // Bring varied groups to both verges, with staggered spacing and open gaps;
 // houses remain behind the trees rather than swallowed by the fog.
 for(let i=0;i<8;i++){
  const side=i%2?1:-1,row=Math.floor(i/2),x=side*(6.3+[0,1.1,.35,2][row]);
  const z=-8+row*5.2+(side>0?.65:-.3),scale=.72+((row*3+(side>0?1:0))%4)*.10;
  tree(template,x,z,scale,(row+(side>0?1:0))%3===0?'Pine_5':'CommonTree_5');
 }
 neighbour(template,-14,-3,Math.PI/2);neighbour(template,14,3,-Math.PI/2);
 for(let z=-10;z<12;z+=3)box(template,.12,.008,1.25,0,.007,z,'cream');
 for(const z of [-8,0,8]){box(template,.09,.7,.09,3.25,.35,z,'white');box(template,.095,.12,.095,3.25,.52,z,'amber');ball(template,4,.25,z+1,.55,.28,.45,'sage');}
 for(const side of [-1,1])for(const centre of [-5,6]){
  for(const offset of [-1.5,0,1.5])box(template,.09,.8,.09,side*4.55,.4,centre+offset,'wood');
  for(const y of [.32,.64])box(template,.065,.06,3.08,side*4.55,y,centre,'wood');
 }
 batch(template);
 const chunkCount=8,chunkLength=24,period=chunkCount*chunkLength,chunkHalfDepth=12;
 for(let i=0;i<chunkCount;i++){const chunk=group(roadside,`roadside-${i}`);chunk.position.z=(i+.5)*chunkLength;}
 // One geometry/material batch is instanced across the entire pool. Empty
 // chunk groups retain transforms for culling, not a duplicate render tree.
 const roadsideBatches=[];
 for(const part of template.children){const mesh=new T.InstancedMesh(part.geometry,part.material,chunkCount);mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);scene.add(mesh);roadsideBatches.push(mesh);}
 let roadPhase=-1,roadCameraZ=Infinity;
 function updateRoadside(distance,cameraZ){
  const phase=distance%period;
  if(phase===roadPhase&&cameraZ===roadCameraZ)return;
  roadPhase=phase;roadCameraZ=cameraZ;
  let active=0;
  for(let i=0;i<chunkCount;i++){
   const chunk=roadside.children[i];chunk.position.z=((i+.5)*chunkLength+phase)%period;
   // The complete chunk crosses the recycle seam outside the close fog.
   chunk.visible=Math.abs(chunk.position.z-cameraZ)-chunkHalfDepth<=scene.fog.far;
   if(chunk.visible){chunk.updateMatrix();for(const mesh of roadsideBatches)mesh.setMatrixAt(active,chunk.matrix);active++;}
  }
  for(const mesh of roadsideBatches){mesh.count=active;mesh.instanceMatrix.needsUpdate=true;mesh.computeBoundingSphere();}
 }
 updateRoadside(0,L.route.start.z);
 const sceneryStats=Object.freeze({trees:24+8*chunkCount,houses:4+2*chunkCount,chunkCount,period,chunkHalfDepth});
 batch(ground);
 // The physical south opening and the parked open leaf use the SAME rectangles
 // as navigation, including the lintel. No decorative hole hides a solid wall.
 for(const s of L.solids.filter(s=>s.id.startsWith('wall-')||s.id==='door-lintel'))box(scenery,s.maxX-s.minX,s.height,s.maxZ-s.minZ,(s.minX+s.maxX)/2,s.minY+s.height/2,(s.minZ+s.maxZ)/2,'cream',s.id);
 const h=L.house,d=L.door,b=L.bench,c=L.cabinet;
 box(scenery,h.width,.16,h.depth,h.x,-.08,h.z,'sand');
 // Shallow base courses stop at the clear opening; the threshold stays below the leaf.
 for(const [a,bx] of [[h.minX,d.minX],[d.maxX,h.maxX]])box(scenery,bx-a,.27,.08,(a+bx)/2,.135,h.maxZ+.125,'sand');
 box(scenery,d.width,.035,.34,d.x,.0175,d.z+.20,'sand');
 // Ceiling fits inside the shell rather than projecting through the eaves.
 box(scenery,h.width-h.thickness,.08,h.depth-h.thickness,h.x,h.height-.04,h.z,'wood');
 pitchedRoof(scenery,{x:h.x,z:h.z,width:h.width,depth:h.depth,eave:h.height,rise:1.28,wallThickness:h.thickness});
 for(const x of [d.minX-.05,d.maxX+.05])box(scenery,.1,d.height,.24,x,d.height/2,d.z,'wood');
 box(scenery,d.width+.2,.1,.24,d.x,d.height+.05,d.z,'wood');
 // Stylised opaque glazing is safely backed by the shell, never a navigable hole.
 for(const x of [-10.5,-4.25]){
  box(scenery,1.15,1.15,.045,x,1.65,4.1125,'sky');
  for(const dx of [-.60,0,.60])box(scenery,.07,1.27,.08,x+dx,1.65,4.14,'wood');
  for(const y of [1.05,2.25])box(scenery,1.28,.06,.07,x,y,4.145,'wood');
  box(scenery,1.38,.12,.30,x,1.0,4.18,'wood');
 }
 // Gutters follow the two eaves; downpipes finish on the plinth, away from the doorway.
 for(const side of [-1,1]){
  box(scenery,h.width+.75,.10,.11,h.x,h.height-.11,side*(h.depth/2+.30),'metal');
  box(scenery,.08,h.height-.18,.08,h.minX+.15,(h.height-.18)/2+.08,side*(h.depth/2+.13),'metal');
  box(scenery,.08,.10,.22,h.minX+.15,h.height-.11,side*(h.depth/2+.23),'metal');
 }
 const door=group(scene,'house-door-hinge');door.position.set(d.hingeX,0,d.z);
 box(door,d.width,d.height,d.thickness,d.width/2,d.height/2,0,'sage','door-leaf');
 for(const y of [.5,1.7])box(door,d.width-.25,.55,.012,d.width/2,y,.034,'cream');
 box(door,.13,.04,.08,d.width-.17,1.05,.06,'amber','door-handle');batch(door);
 // Bench supports contact both ground and underside of its slab exactly.
 box(scenery,b.width,b.slab,b.depth,b.x,b.top-b.slab/2,b.z,'wood');
 for(const sx of [-1,1])for(const sz of [-1,1])box(scenery,b.leg,b.top-b.slab,b.leg,b.x+sx*(b.width/2-b.inset),(b.top-b.slab)/2,b.z+sz*(b.depth/2-b.inset),'ink');
 box(scenery,b.width-.2,.08,.06,b.x,.18,b.z,'ink');
 // Outlines enclose each object's footprint over its allowed centre range.
 // Labels sit along the front edge, away from both the objects and the open case.
 const guides=group(scene,'guided-kit');guides.visible=false;
 const guideOrder=Object.entries(L.guidedKit.targets).sort((a,b)=>a[1].x-b[1].x);
 guideOrder.forEach(([id,p],i)=>{
  const g=group(guides,`guide:${id}`),q=L.itemDimensions[id],tol=L.guidedKit.tolerance;
  g.position.set(p.x,b.top+.003,p.z);
  const width=q.width+2*tol,depth=q.depth+2*tol,stroke=.007,col=L.guidedKit.colors[id].material;
  for(const z of [-depth/2,depth/2])box(g,width,.004,stroke,0,0,z,col);
  for(const x of [-width/2,width/2])box(g,stroke,.004,depth,x,0,0,col);
  batch(g); // Four static outline bars share one draw submission per guide.
  // A small cross identifies the centre: the readiness tolerance is only 2 cm.
  const center=group(g,`guide-center:${id}`);
  box(center,.035,.004,.005,0,.003,0,col);
  box(center,.005,.004,.035,0,.003,0,col);
  batch(center);
  const label=group(guides,`guide-label:${id}`);label.userData.fr=itemName('fr',id);label.userData.en=itemName('en',id);
  label.position.set(b.x-b.width/2+.24+i*.48,b.top+.09,b.z+b.depth/2+.085);
  // Canvas is only needed in the browser; Node can still construct the geometry.
  if(typeof document!=='undefined'){
   const canvas=document.createElement('canvas');canvas.width=512;canvas.height=128;
   const ctx=canvas.getContext('2d');
   if(ctx){ctx.fillStyle=colors[col];ctx.fillRect(0,0,512,128);ctx.fillStyle='#fff9e9';ctx.textAlign='center';ctx.font='bold 38px sans-serif';ctx.fillText(label.userData.fr,256,49,490);ctx.font='32px sans-serif';ctx.fillText(label.userData.en,256,99,490);
    const texture=new T.CanvasTexture(canvas);texture.colorSpace=T.SRGBColorSpace;
    const panel=new T.Mesh(new T.PlaneGeometry(.43,.105),new T.MeshBasicMaterial({map:texture,side:T.DoubleSide}));label.add(panel);
   }
  }
 });
 // Recognisable filled reference-proportioned cabinet, two rows, 13 pitches.
 const board=group(scenery,'training-cabinet');board.position.set(c.x,c.y,c.backZ);
 box(board,c.width,c.height,.014,0,0,.007,'cream','cabinet-back');
 for(const x of [-1,1])box(board,.014,c.height,c.depth,x*(c.width/2-.007),0,c.depth/2,'white');
 for(const y of [-1,1])box(board,c.width-.028,.014,c.depth,0,y*(c.height/2-.007),c.depth/2,'white');
 for(const y of [-c.railPitch/2,c.railPitch/2]){
  box(board,c.width-.04,.07,.012,0,y,.05,'metal');
  for(const dy of [-.03,.03])box(board,c.width-.04,.008,.018,0,y+dy,.062,'metal');
 }
 // The lower row is already populated; top row retains real blanks beside the
 // apprentice slots. Neither empty shell nor real wiring instruction.
 for(let row=0;row<2;row++)for(let i=0;i<13;i++){
  if(row===0&&i<4)continue;
  const x=-.234+(i+.5)*c.module,y=c.railPitch*(.5-row);
  box(board,c.module-.002,.178,.12,x,y,.123,'white');
  if(row===1&&i<7){box(board,.019,.033,.015,x,y-.012,.19,'ink');box(board,.017,.007,.003,x,y+.035,.185,'blue');}
  else box(board,.001,.16,.003,x+.014,y,.185,'sand');
 }
 for(const y of [-.29,0,.29])box(board,c.width-.03,y===0?.065:.10,.018,0,y,c.depth-.009,'cream');
 // Printed geometric safety motif: unplugged training equipment, no mains values.
 box(scenery,.9,.65,.025,-6.6,1.75,-3.89,'wood');
 const poster=new T.Mesh(new T.PlaneGeometry(.84,.59),new T.MeshBasicMaterial({color:colors.cream,fog:true}));poster.position.set(-6.6,1.75,-3.875);scene.add(poster);
 for(let i=0;i<3;i++)box(scenery,.15,.09,.006,-6.87+i*.27,1.85,-3.868,i===2?'amber':'sage');
 box(scenery,.6,.025,.006,-6.6,1.6,-3.868,'ink');
 // Van with honest driver-eye sight line: open windshield aperture, pillars,
 // dashboard, passenger seat and separate hinged passenger door.
 const van=group(scene,'electrician-van');
 box(van,1.95,.23,4.7,0,.39,0,'ink');
 box(van,1.95,1.84,2.55,0,1.48,1.075,'white');
 box(van,1.95,.13,2.15,0,2.36,-1.2,'white');
 box(van,1.9,.52,.75,0,.82,-1.97,'white');
 box(van,1.55,.18,.08,0,.59,-2.37,'ink');
 for(const x of [-.83,.83])box(van,.25,.15,.025,x,.93,-2.356,'amber');
 for(const x of [-.91,.91]){
  box(van,.065,1.35,.09,x,1.62,-2.20,'white');
  box(van,.08,1.6,.09,x,1.55,-.32,'white');
  // Cab side skins belong to the hinged doors, not a fixed duplicate panel.
  box(van,.045,.11,2.2,x*1.075,1.36,1.06,'sage');
  for(const z of [-1.56,1.5]){const wheel=cyl(van,.36,.16,x*1.08,.36,z,'black');wheel.rotation.z=Math.PI/2;const hub=cyl(van,.17,.175,x*1.08,.36,z,'metal');hub.rotation.z=Math.PI/2;}
 }
 box(van,1.76,.15,.44,0,1.19,-1.91,'ink');
 // The lower windshield cowl overlaps both dashboard and wiper spindles.
 box(van,1.76,.05,.14,0,1.275,-2.16,'ink');
 const wheel=group(van,'steering-wheel');wheel.position.set(-.52,1.22,-1.53);wheel.rotation.x=-.4;
 wheel.add(new T.Mesh(new T.TorusGeometry(.18,.018,6,16),materials.ink));
 box(wheel,.075,.07,.04,0,0,.006,'ink');
 for(const a of [-.35,Math.PI+.35,Math.PI/2]){const spoke=box(wheel,.025,.15,.022,Math.sin(a)*.08,Math.cos(a)*.08,0,'ink');spoke.rotation.z=-a;}
 const column=cyl(van,.026,Math.hypot(.22,.23),-.52,1.11,-1.785,'ink');column.quaternion.setFromUnitVectors(new T.Vector3(0,1,0),new T.Vector3(0,.22,.23).normalize());
 box(van,.31,.12,.10,-.52,1.325,-1.94,'ink');
 for(const x of [-.59,-.45]){const dial=cyl(van,.038,.008,x,1.33,-1.887,'cream');dial.rotation.x=Math.PI/2;box(van,.006,.045,.003,x,1.337,-1.880,'ink');}
 for(const x of [-.52,.52]){box(van,.53,.12,.55,x,.76,-.98,'blue');box(van,.53,.58,.12,x,1.09,-.67,'blue');}
 // Roof ladder makes the trade read from a distance.
 for(const x of [-.42,.42])box(van,.07,.055,2.9,x,2.49,.6,'metal');
 for(let z=-.7;z<2;z+=.36)box(van,.84,.05,.04,0,2.49,z,'metal');
 van.remove(wheel);batch(van);van.add(wheel);
 const passengerDoor=group(van,'passenger-door-hinge');passengerDoor.position.set(.975,.54,-2.13);
 box(passengerDoor,.06,.65,1.76,0,.325,.88,'white');box(passengerDoor,.065,.05,1.76,0,1.26,.88,'white');box(passengerDoor,.065,.61,.06,0,.945,1.73,'white');box(passengerDoor,.075,.045,.13,.04,.53,1.40,'ink');batch(passengerDoor);
 const mirror=group(passengerDoor,'door-mirror');mirror.position.set(.145,.96,.21);
 box(mirror,.28,.22,.14,0,0,0,'ink');box(mirror,.235,.175,.015,0,0,.083,'mirrorGlass');box(mirror,.19,.04,.05,-.14,-.08,-.02,'ink');batch(mirror);
 const driverDoor=passengerDoor.clone(true);driverDoor.name='driver-door-hinge';driverDoor.position.x=-.975;driverDoor.scale.x=-1;van.add(driverDoor);
 const cabDetails=group(van,'cab-close-details');
 for(const sign of [-1,1]){

  const belt=box(cabDetails,.045,.66,.022,sign*.52,1.05,-.75,'black');belt.rotation.z=sign*.36;
  box(cabDetails,.12,.035,.1,sign*.20,.84,-1.04,'coral');
  for(let i=0;i<4;i++)box(cabDetails,.01,.06,.008,sign*.70+i*.022,1.29,-1.735,'metal');
  const wiper=box(cabDetails,.32,.018,.025,sign*.42,1.39,-2.212,'ink');wiper.rotation.z=sign*.16;
  // Spindle, arm and blade physically overlap at the cowl and glass.
  cyl(cabDetails,.025,.025,sign*.42,1.31,-2.205,'black').rotation.x=Math.PI/2;
  box(cabDetails,.022,.085,.025,sign*.42,1.35,-2.212,'black');
 }
 box(cabDetails,.30,.12,.025,0,1.25,-1.675,'metal');box(cabDetails,.17,.07,.012,0,1.25,-1.654,'black');
 for(const xx of [-.11,.11])cyl(cabDetails,.015,.035,xx,1.23,-1.64,'ink').rotation.x=Math.PI/2;
 box(cabDetails,.28,.10,.18,.22,1.32,-1.83,'sand');box(cabDetails,.24,.012,.16,.22,1.377,-1.83,'cream');
 batch(cabDetails);
 // A lightly tinted pane spans the real aperture; the wipers sit on its outer face.
 const windscreen=new T.Mesh(new T.PlaneGeometry(1.78,.93),new T.MeshLambertMaterial({color:colors.sky,transparent:true,opacity:.10,side:T.DoubleSide,depthWrite:false}));
 windscreen.name='windscreen';windscreen.position.set(0,1.825,-2.20);van.add(windscreen);
 // Camille: simple low-poly adult, blue work clothes with small ochre vest accents, two-eyed face,
 // hair masses fused to the skull, and rounded joints so limbs read as one continuous body.
 const mentor=group(scene,'Camille');
 const torso=group(mentor,'torso');
 box(torso,.37,.44,.22,0,1.14,0,'blue');ball(torso,0,1.345,0,.2,.05,.11,'blue');
 for(const x of [-.10,.10]){box(torso,.06,.36,.016,x,1.18,-.113,'amber');box(torso,.06,.016,.235,x,1.362,0,'amber');}
 box(torso,.085,.07,.014,.11,.99,-.113,'amber');box(torso,.375,.025,.225,0,.935,0,'cream');
 box(torso,.35,.14,.21,0,.89,0,'blue');box(torso,.375,.045,.225,0,.915,0,'ink');
 cyl(torso,.05,.14,0,1.42,0,'skin');cyl(torso,.078,.04,0,1.375,0,'blue');
 ball(torso,0,1.60,0,.12,.15,.115,'skin');
 // Hair: short crop. Crown stops above the ear, a nape plane sits behind it, a swept fringe plane crosses the forehead.
 ball(torso,0,1.685,.02,.126,.09,.12,'hair');ball(torso,0,1.625,.065,.10,.075,.075,'hair');
 for(const x of [-.097,.097])box(torso,.035,.06,.07,x,1.645,-.01,'hair');
 // Flat, near-flush eyes and ears (thin discs set into the face, not spheres on it).
 for(const x of [-.045,.045])ball(torso,x,1.62,-.102,.012,.017,.005,'ink');
 ball(torso,0,1.585,-.112,.017,.02,.012,'skin');
 for(const x of [-.116,.116])ball(torso,x,1.60,.005,.008,.03,.02,'skin');batch(torso);
 const legs=[],arms=[];
 for(const sign of [-1,1]){
  const leg=group(mentor,`hip-${sign}`);leg.position.set(sign*.105,.86,0);ball(leg,0,0,0,.085,.085,.085,'blue');cyl(leg,.075,.39,0,-.195,0,'blue');batch(leg);
  const knee=group(leg,`knee-${sign}`);knee.position.y=-.39;ball(knee,0,0,0,.068,.068,.068,'blue');cyl(knee,.06,.38,0,-.19,0,'blue');box(knee,.12,.07,.21,0,-.435,-.04,'ink');ball(knee,0,-.43,-.14,.06,.04,.05,'ink');batch(knee);legs.push({hip:leg,knee});
  const arm=group(mentor,`shoulder-${sign}`);arm.position.set(sign*.24,1.31,0);box(arm,.11,.07,.115,0,-.015,0,'blue').rotation.z=-sign*.15;cyl(arm,.052,.28,0,-.14,0,'blue');batch(arm);
  const elbow=group(arm,`elbow-${sign}`);elbow.position.y=-.28;ball(elbow,0,0,0,.05,.05,.05,'blue');cyl(elbow,.045,.25,0,-.125,0,'blue');cyl(elbow,.05,.03,0,-.235,0,'cream');cyl(elbow,.03,.06,0,-.26,0,'skin');
  // Hand: palm wedge, four grooved fingers and an opposed thumb round the rim. Built in steering-wheel axes (X radial,
  // Y along the rim, Z towards the driver) and oriented into the elbow frame by the basis measured for the seated pose.
  // Parts are written for the right hand (u = outward along the rim radius, mirrored by sign for the left). The rim is a
  // 16-sided polygon (R .18, hexagonal tube r .018) with a corner exactly at the grip, its core .010 outward of the palm
  // point; both straight sides lean inward by atan(tan(PI/16)), so bend() shears hand-frame (u,y) along them and the
  // finger tunnel follows the rim instead of cutting it.
  const grip=group(elbow,'hand-grip');grip.position.set(0,-.29,0);
  const gx=new T.Vector3(.91,-sign*.22,-sign*.37).normalize(),gy0=new T.Vector3(-sign*.42,-.62,-.67),gy=gy0.sub(gx.clone().multiplyScalar(gx.dot(gy0))).normalize();
  grip.quaternion.setFromRotationMatrix(new T.Matrix4().makeBasis(gx,gy,new T.Vector3().crossVectors(gx,gy)));
  const u0=.010,rt=.0215,lean=Math.tan(Math.PI/16),rad=Math.PI/180,pt=(a,r)=>[u0+r*Math.cos(a*rad),r*Math.sin(a*rad)];
  const bend=(u,y)=>[u-lean*Math.abs(y),y];
  // Palm liner: two thin plates in front of and behind the rim tunnel (compact .04 x .07 x .05 palm block centred on the palm point).
  for(const z of [-.02125,.02125])box(grip,.04,.07,.0075,0,0,z,'skin');
  // Chamfered C-prism round the rim: outer profile [[angle from the outward radius towards the driver, radius from the rim
  // core]], tunnel of radius rt (a few facets, still clear of the hexagonal tube) spanning y0..y1 along the rim; the end
  // bevels b are the grooves between fingers.
  const cprism=(prof,inner,y0,y1,b,steps=1)=>{
   const pts=[...prof.map(([a,r])=>pt(a,r)),...inner.map(i=>pt(prof[i][0],rt)).reverse()];
   const g=new T.ExtrudeGeometry(new T.Shape(pts.map(([u,z])=>new T.Vector2(sign*u,z))),{depth:y1-y0-2*b,steps,curveSegments:1,bevelEnabled:true,bevelThickness:b,bevelSize:b,bevelOffset:-b,bevelSegments:1});
   g.rotateX(Math.PI/2);g.translate(0,y1-b,0);
   const p=g.attributes.position;for(let i=0;i<p.count;i++){const [bu,by]=bend(sign*p.getX(i),p.getY(i));p.setXYZ(i,sign*bu,by,p.getZ(i));}
   g.computeVertexNormals();grip.add(new T.Mesh(g,materials.skin));};
  // Palm and back of the hand: one smooth wedge on the driver side, folded at the rim corner, running down over the wrist end.
  cprism([[158,.036],[132,.0405],[105,.042],[78,.0415],[52,.040]],[0,2,4],-.042,.042,.003,2);
  // Four grooved fingers (little, ring, middle, index) either side of the rim corner: knuckle ridge on the outer face,
  // crease at the second joint, tip curling in.
  const knuckle=[[62,.0405],[40,.043],[16,.0415],[-8,.039],[-24,.0355],[-46,.0375],[-80,.0375],[-118,.0345]];
  for(const [y0,y1,s,tip] of [[-.038,-.019,.90,-132],[-.019,0,.96,-150],[0,.019,1,-160],[.019,.038,.97,-146]])
   cprism([...knuckle,[tip,.0285]].map(([a,r])=>[a,rt+(r-rt)*s]),[0,2,4,6,7,8],y0,y1,.0035);
  // Opposed thumb: base pad on the back of the hand, then tapered segments curving over the rim's driver side so the tip
  // turns in towards the inner face of the tube (opposite the fingertips). Low-resolution joints keep it faceted.
  const blob=new T.SphereGeometry(1,6,4);
  const knob=(u,y,z,r)=>{const o=new T.Mesh(blob,materials.skin);o.position.set(sign*u,y,z);o.scale.setScalar(r);grip.add(o);};
  const link=(a,b,r0,r1)=>{const p=new T.Vector3(sign*a[0],a[1],a[2]),q=new T.Vector3(sign*b[0],b[1],b[2]),d=q.clone().sub(p);
   const m=new T.Mesh(new T.CylinderGeometry(r1,r0,d.length(),6),materials.skin);m.position.copy(p).addScaledVector(d,.5);m.quaternion.setFromUnitVectors(new T.Vector3(0,1,0),d.normalize());grip.add(m);};
  const thumb=[[120,.0455,.010,.0130],[128,.040,.030,.0112],[148,.0325,.047,.0098],[172,.0285,.058,.0085]].map(([a,r,y,rr])=>{const [u,z]=pt(a,r),[bu,by]=bend(u,y);return [bu,by,z,rr];});
  thumb.forEach(([u,y,z,r],i)=>{knob(u,y,z,r);if(i)link(thumb[i-1],[u,y,z],thumb[i-1][3],r);});
  batch(elbow);arms.push({shoulder:arm,elbow});
 }
 mentor.userData={legs,arms};
 // Hinged toolbox: tray and real lid, individual recognisable removable objects.
 const tb=L.toolbox,toolbox=group(scene,'toolbox');toolbox.position.set(tb.x,tb.y,tb.z);
 box(toolbox,tb.width,.025,tb.depth,0,.0125,0,'ink');
 for(const x of [-1,1])box(toolbox,.025,tb.height,tb.depth,x*(tb.width/2-.0125),tb.height/2,0,'coral');
 box(toolbox,tb.width-.05,tb.height,.025,0,tb.height/2,-(tb.depth/2-.0125),'coral');
 for(const x of [-.31,.31])cyl(toolbox,.018,.10,x,tb.height,-tb.depth/2,'metal').rotation.z=Math.PI/2;
 batch(toolbox);
 const frontFlap=group(toolbox,'case-front-flap');frontFlap.position.set(0,.0125,tb.depth/2-.0125);
 box(frontFlap,tb.width-.05,tb.height-.0125,.025,0,(tb.height-.0125)/2,0,'coral');batch(frontFlap);
 const lid=group(toolbox,'case-lid');lid.position.set(0,tb.height,-tb.depth/2);
 box(lid,tb.width,.045,tb.depth,0,.0225,tb.depth/2,'coral');
 box(lid,.22,.045,.06,0,.09,tb.depth/2,'ink');for(const x of [-.12,.12])box(lid,.025,.07,.06,x,.055,tb.depth/2,'ink');batch(lid);
 // Cache only the case shell and its hinges, never the town or held tools.
 const caseMeshes=[];toolbox.traverse(o=>{if(o.isMesh)caseMeshes.push(o);});
 const pickCandidates=[],pickHits=[],caseHits=[];
 function inheritedVisible(o){for(let p=o;p;p=p.parent)if(!p.visible)return false;return true;}
 function caseSurfaceHit(ray){
  caseHits.length=0;ray.intersectObjects(caseMeshes,false,caseHits);
  return caseHits.find(hit=>{const material=Array.isArray(hit.object.material)?hit.object.material[hit.face.materialIndex]:hit.object.material;return inheritedVisible(hit.object)&&material?.visible!==false&&(!material?.transparent||material.opacity>=1);})??null;
 }
 function pickTarget(ray,candidates=targets){
  pickCandidates.length=pickHits.length=caseHits.length=0;
  for(const o of candidates)if(inheritedVisible(o))pickCandidates.push(o);
  const hit=ray.intersectObjects(pickCandidates,false,pickHits)[0];
  // Clicking the case's own shell must still open it. Invisible proxy materials
  // are selectable, but are not rendered opaque surfaces.
  if(!hit||hit.object.userData.id==='toolbox')return hit??null;
  const far=ray.far;ray.far=Math.min(far,hit.distance-1e-5);
  ray.intersectObjects(caseMeshes,false,caseHits);ray.far=far;
  for(const block of caseHits){const material=Array.isArray(block.object.material)?block.object.material[block.face.materialIndex]:block.object.material;if(inheritedVisible(block.object)&&material?.visible!==false&&(!material?.transparent||material.opacity>=1))return null;}
  return hit;
 }
 const items={},homes={},ids=Object.keys(L.itemDimensions);
 // Case is spacious enough for the disassembled frame, with tools in a separate
 // front compartment. Every item origin is its lowest physical support plane.
 const positions={frame:[-.18,0,-.015],driver:[.34,0,-.15],pliers:[.23,0,.16],differential:[.11,0,-.18],'breaker-a':[.20,0,-.18],'breaker-b':[.27,0,-.18]};
 let driver;
 for(const id of ids){
  const root=group(scene,id),inner=group(root,`${id}-model`);items[id]=root;
  if(id==='driver'){
   driver=inner;const s=L.driver;
   const handle=new T.Mesh(new T.LatheGeometry(s.handleProfile.map(([r,y])=>new T.Vector2(r,y)),12),materials.amber);handle.name='handle';inner.add(handle);
   cyl(inner,s.shaftRadius,s.shaftEnd-s.bladeLength,0,(s.shaftEnd+s.bladeLength)/2,0,'metal').name='shaft';
   box(inner,s.bladeWidth,s.bladeLength,s.bladeThickness,0,s.bladeLength/2,0,'metal','blade');
   for(const y of [.25,.29])cyl(inner,y===.25?.026:.021,.008,0,y,0,'ink');
   const tilt=Math.atan(Math.max(...s.handleProfile.map(([r,y])=>(r-s.bladeThickness/2)/y)));
   inner.rotation.x=Math.PI/2-tilt;inner.position.set(0,s.bladeThickness/2*Math.cos(tilt),-.15);
  }else if(id==='pliers'){
   for(const sign of [-1,1]){
    const handle=box(inner,.027,.045,.18,sign*.038,.0225,.045,'sage');handle.rotation.y=sign*.25;
    const grip=box(inner,.026,.006,.058,sign*.053,.043,.105,'black');grip.rotation.y=sign*.25;
    const jaw=box(inner,.025,.035,.105,sign*.022,.0225,-.088,'metal');jaw.rotation.y=-sign*.2;
   }
   cyl(inner,.027,.048,0,.024,-.025,'metal');batch(inner);
  }else if(id==='frame'){
   for(const x of [-1,1])box(inner,.018,.035,.61,x*.216,.0175,0,'white');
   for(const z of [-1,1])box(inner,.414,.035,.025,0,.0175,z*.2925,'white');
   box(inner,.414,.035,.032,0,.0175,0,'white');batch(inner);
  }else{
   const width=L.itemDimensions[id].width;
   box(inner,width,.18,.105,0,.09,0,'white');
   for(const y of [.025,.155])box(inner,width-.003,.035,.015,0,y,.06,'cream');
   box(inner,id==='differential'?.04:.019,.033,.018,id==='differential'?-.008:0,.075,.061,'ink');
   box(inner,width*.5,.008,.002,0,.124,.054,'blue');
   if(id==='differential'){const test=cyl(inner,.006,.012,.022,.123,.06,'amber');test.rotation.x=Math.PI/2;}
   batch(inner);
  }
  root.updateWorldMatrix(true,true);const bb=new T.Box3().setFromObject(root,true);
  // Normalize to exact floor/bench contact without changing the model scale.
  inner.position.y-=bb.min.y;
  homes[id]={innerPosition:inner.position.clone(),innerRotation:inner.rotation.clone()};
  const [x,y,z]=positions[id];root.position.set(tb.x+x,tb.y+.027+y,tb.z+z);homes[id].position=root.position.clone();
 }
 const handView=new T.Group();handView.name='held-item-view';handView.position.set(.27,-.24,-.55);
 const handItems={};for(const id of ids){const clone=items[id].clone(true);clone.name=`held-${id}`;clone.position.set(0,0,0);clone.visible=false;handView.add(clone);handItems[id]=clone;}
 const screw=group(scene,'cover-screw');screw.position.set(c.x,c.y-c.height/2+.065,c.backZ+c.depth+.022);
 cyl(screw,.011,.012,0,0,0,'metal').rotation.x=Math.PI/2;
 box(screw,.014,.002,.0006,0,0,.0063,'ink');screw.userData={home:screw.position.clone(),travel:.006,face:.0066};
 const cover=group(scene,'cover');cover.position.set(c.x,c.y,c.backZ+c.depth+.012);
 // Windowed cover leaves all module levers visible, no coplanar overlays.
 for(const x of [-1,1])box(cover,.014,c.height-.03,.018,x*(c.width/2-.02),0,0,'white');
 for(const y of [-.29,0,.29])box(cover,c.width-.04,.045,.018,0,y,0,'white');batch(cover);
 const targets=[],targetMap=new Map(),proxyMaterial=new T.MeshBasicMaterial({visible:false});
 function target(id,w,h,d){const o=new T.Mesh(unit,proxyMaterial);o.scale.set(w,h,d);o.userData.id=id;scene.add(o);targets.push(o);targetMap.set(id,o);return o;}
 target('mentor',.50,1.65,.40);target('toolbox',tb.width,.15,tb.depth);
 const vanDoorTarget=target('van-door',.16,.75,.8);passengerDoor.add(vanDoorTarget);vanDoorTarget.position.set(.01,.86,.43);
 target('house-door',.34,.55,.06).position.set(d.maxX-.19,1.12,d.z+.13);
 // Switch plate is mounted on the inner south face, clear of the door sweep.
 const switchZ=h.maxZ-h.thickness/2;
 const wallSwitch=group(scene,'light-switch');wallSwitch.position.set(-7.45,1.30,switchZ);
 box(wallSwitch,.16,.19,.025,0,0,-.0125,'white');
 box(wallSwitch,.085,.105,.012,0,0,-.031,'ink');
 box(wallSwitch,.065,.080,.008,0,0,-.041,'cream');
 const switchTarget=target('light-switch',.16,.19,.049);switchTarget.position.set(-7.45,1.30,switchZ-.0245);
 for(const id of ids){const q=L.itemDimensions[id];target(`item:${id}`,q.width+.008,q.height+.008,q.depth+.008);target(`drop:${id}`,q.width+.008,q.height+.008,q.depth+.008);}
 const slots={frame:{x:c.x,y:c.y,z:c.backZ+c.depth+.007},differential:{x:c.x-.198,y:c.y+c.railPitch/2,z:c.backZ+.19},'breaker-a':{x:c.x-.144,y:c.y+c.railPitch/2,z:c.backZ+.19},'breaker-b':{x:c.x-.108,y:c.y+c.railPitch/2,z:c.backZ+.19}};
 const slotGhosts={};
 for(const [id,p] of Object.entries(slots)){
  const q=target(`slot:${id}`,id==='frame'?.46:L.itemDimensions[id].width,.18,.018);q.position.copy(p);
  const ghost=group(scene,`socket-${id}`);ghost.position.set(p.x,p.y,p.z-.004);
  if(id!=='frame'){for(const x of [-1,1])box(ghost,.002,.18,.003,x*(L.itemDimensions[id].width/2-.002),0,0,'amber');for(const y of [-1,1])box(ghost,L.itemDimensions[id].width,.002,.003,0,y*.09,0,'amber');}
  batch(ghost);slotGhosts[id]=ghost;
 }
 target('screw',.034,.034,.018);
 const teleports=[];
 for(const a of L.anchors){
  const m=new T.Mesh(new T.CircleGeometry(.28,20),new T.MeshBasicMaterial({visible:false,side:T.DoubleSide}));m.position.set(a.x,.008,a.z);m.rotation.x=-Math.PI/2;m.userData={id:a.id,anchor:a};m.visible=false;
  const ring=new T.Mesh(new T.RingGeometry(.21,.245,20),new T.MeshBasicMaterial({color:colors.sage,side:T.DoubleSide}));m.add(ring);scene.add(m);teleports.push(m);
 }
 const panelMount=group(scene,'xr-panel');panelMount.position.set(-8.8,1.7,-3.45);
 const focusRing=new T.Box3Helper(new T.Box3(),colors.amber);focusRing.visible=false;scene.add(focusRing);
 batch(scenery);
 if(floorTexture)scene.add(createInteriorFinishes(floorTexture));
 // Preserve authored glTF materials/UVs. Place each model by its real bounds,
 // not its arbitrary export origin; never merge furnishings into wall geometry.
 let lamp;
 if(interior){
  function furnished(id,name,x,y,z,ceiling=false){
   const model=interior[id];if(!model?.isObject3D)throw new Error(`Missing interior asset ${id}`);
   model.updateWorldMatrix(true,true);const bounds=new T.Box3().setFromObject(model,true);
   if(bounds.isEmpty())throw new Error(`Empty interior asset ${id}`);
   const center=bounds.getCenter(new T.Vector3()),root=group(scene,name);
   root.add(model);model.position.x-=center.x;model.position.z-=center.z;model.position.y-=ceiling?bounds.max.y:bounds.min.y;
   root.position.set(x,y,z);root.updateWorldMatrix(true,true);return root;
  }
  furnished('chinese_armchair','furnishing-chair',L.furniture.chair.x,0,L.furniture.chair.z);
  const table=furnished('round_wooden_table_01','furnishing-table',L.furniture.table.x,0,L.furniture.table.z);
  const tableTop=new T.Box3().setFromObject(table,true).max.y;
  furnished('potted_plant_04','furnishing-plant',L.furniture.table.x,tableTop,L.furniture.table.z);
  lamp=furnished('modern_ceiling_lamp_01','furnishing-ceiling-lamp',-5.1,h.height-.08,-.15,true);
 }
 else{lamp=group(scene,'furnishing-ceiling-lamp');lamp.position.set(-5.1,h.height-.08,-.15);}
 // An independent diffuser extends just below the shade. No imported or shared
 // material is modified; intensity changes retain the same compiled light count.
 lamp.updateWorldMatrix(true,true);
 const lampBounds=new T.Box3().setFromObject(lamp,true),bottom=lampBounds.isEmpty()?lamp.position.y-.95:lampBounds.min.y;
 const globe=new T.Mesh(new T.SphereGeometry(.105,16,12),new T.MeshStandardMaterial({color:'#e5d8b9',emissive:'#ffd18b',emissiveIntensity:0,roughness:.7}));
 globe.name='ceiling-globe';globe.position.set(0,bottom-lamp.position.y-.025,0);lamp.add(globe);
 const ceilingLight=new T.PointLight('#ffd19a',0,9,2);ceilingLight.name='ceiling-light';ceilingLight.position.set(0,globe.position.y-.10,0);lamp.add(ceilingLight);
 const siteBounds=new T.Box3().setFromObject(scenery,true),cullPoint=new T.Vector3();
 scene.add(new T.HemisphereLight('#d5deed','#6b746e',1.65));const sun=new T.DirectionalLight('#ffd3a0',1.35);sun.position.set(-9,5,8);scene.add(sun);
 let usingTool=false,lastUsingTool=false,reducedMotion=false;
 let lastState=null,lastStage=null,waypoint=0,walkClock=0,doorAmount=0;
 const vanPose={...L.van.park};
 const temp=new T.Vector3(),boxBounds=new T.Box3(),screwAxis=new T.Vector3(0,1,0),outAxis=new T.Vector3(0,0,1),flat=new T.Quaternion().setFromUnitVectors(screwAxis,outAxis);
 function poseVan(s){
  const time=Math.max(0,s.time||0),p=vanPose;
  p.x=L.van.park.x;p.z=L.van.park.z;p.yaw=L.van.park.yaw;
  if(s.stage==='drive'){p.x=L.route.start.x;p.z=L.route.start.z;p.yaw=0;}
  else if(s.stage==='turn'){p.x=0;p.z=T.MathUtils.lerp(L.route.start.z,L.route.turn.z,Math.min(time/DURATIONS.approach,1));p.yaw=0;}
  else if(s.stage==='park'){
   const t=Math.min(time/DURATIONS.park,1),u=t*t*(3-2*t);
   // Quarter-circle left turn, followed by the straight driveway.
   if(u<.5){const a=u*Math.PI;p.x=-2+2*Math.cos(a);p.z=12-2*Math.sin(a);p.yaw=a;}
   else{p.x=-2-8*(u-.5);p.z=10;p.yaw=Math.PI/2;}
  }
  return p;
 }
 function cameraPose(s){
  if(!CINEMATIC.has(s.stage))return null;
  if(['recall','brief'].includes(s.stage))return {...L.spawn,y:L.eyeHeight,lookFactor:0};
  const p=poseVan(s),exiting=s.stage==='exit',time=Math.max(0,s.time||0);
  const u=exiting?T.MathUtils.smoothstep(time,0,DURATIONS.exit):0;
  const step=exiting?T.MathUtils.smoothstep(time,.7,DURATIONS.exit):0;
  const x=T.MathUtils.lerp(L.van.seat.x,1.95,step),z=L.van.seat.z;
  return {x:p.x+Math.cos(p.yaw)*x+Math.sin(p.yaw)*z,y:L.eyeHeight+(exiting&&!reducedMotion?Math.sin(step*Math.PI)*.14:0),z:p.z-Math.sin(p.yaw)*x+Math.cos(p.yaw)*z,yaw:p.yaw*(1-u),pitch:0,lookFactor:1-u};
 }
 function spawn(){return {...L.spawn};}
 function targetPosition(id){const o=targetMap.get(id);return o?o.getWorldPosition(new T.Vector3()):null;}
 function mountedPose(id){
  const o=items[id],p=slots[id];o.rotation.set(0,0,0);
  if(id==='frame'){o.rotation.x=Math.PI/2;o.position.set(p.x,p.y,p.z-.035);}
  else o.position.set(p.x,p.y-.09,c.backZ+.128);
  o.visible=true;o.userData.mounted=true;
 }
 function resetItem(id){const o=items[id],inner=o.children[0],home=homes[id];o.rotation.set(0,0,0);o.position.copy(home.position);inner.position.copy(home.innerPosition);inner.rotation.copy(home.innerRotation);inner.visible=true;o.userData.mounted=false;}
 function sync(s){
  lastState=s;
  guides.visible=s.stage==='collect';
  const collect=['collect','practice','fasten'].includes(s.stage),installed=new Set(s.installed||[]),held=s.held;

  const open=!['drive','turn','park','van-door','exit','recall','brief','follow','toolbox'].includes(s.stage);
  lid.rotation.x=open?-1.95:0;
  frontFlap.rotation.x=open?Math.PI/2:0;
  for(const id of ids){
   resetItem(id);const o=items[id],drop=s.placed?.[id];
   if(installed.has(id)&&slots[id])mountedPose(id);
   else if(drop){o.position.set(drop.x,drop.y,drop.z);o.visible=true;}
   else o.visible=open&&held!==id;
   const pick=targetMap.get(`item:${id}`),placed=targetMap.get(`drop:${id}`);
   pick.visible=collect&&o.visible&&!drop&&!installed.has(id);placed.visible=collect&&o.visible&&!!drop;
   if(pick.visible||placed.visible){o.updateWorldMatrix(true,true);boxBounds.setFromObject(o,true);boxBounds.getCenter(temp);pick.position.copy(temp);placed.position.copy(temp);boxBounds.getSize(temp);pick.scale.copy(temp).addScalar(.008);placed.scale.copy(pick.scale);}
  }

  for(const id of Object.keys(slots)){
   const eligible=s.stage==='practice'&&id==='breaker-b'&&!installed.has(id);
   targetMap.get(`slot:${id}`).visible=eligible;slotGhosts[id].visible=eligible;
  }
  const toolTarget=targetMap.get('toolbox');toolTarget.visible=s.stage==='toolbox';toolTarget.position.set(tb.x,tb.y+tb.height/2,tb.z);
  vanDoorTarget.visible=s.stage==='van-door';targetMap.get('house-door').visible=s.stage==='follow'&&!s.houseDoorOpen;
  switchTarget.visible=s.stage==='turn-on-light';
  const lightOn=s.stage==='complete';ceilingLight.intensity=lightOn?18:0;globe.material.emissiveIntensity=lightOn?2:0;
  targetMap.get('mentor').visible=['recall','brief','follow','collect','ready','walk-to-board','practice','fasten','debrief','turn-on-light','complete'].includes(s.stage);
  const progress=T.MathUtils.clamp(s.screw||0,0,1);
  screw.position.copy(screw.userData.home);screw.position.z-=progress*screw.userData.travel;screw.rotation.z=progress*Math.PI*4;
  targetMap.get('screw').position.copy(screw.position);targetMap.get('screw').position.z+=screw.userData.face+.006;targetMap.get('screw').visible=s.stage==='fasten';
  cover.visible=['fasten','debrief','turn-on-light','complete'].includes(s.stage);
  handView.visible=!!handItems[held]&&!usingTool;
  for(const id of ids)handItems[id].visible=held===id;
  if(s.stage==='fasten'&&held==='driver'&&usingTool){
   const root=items.driver;root.position.set(0,0,0);root.rotation.set(0,0,0);root.visible=true;driver.visible=true;
   driver.position.copy(screw.position);driver.position.z+=screw.userData.face;
   driver.quaternion.copy(screw.quaternion).multiply(flat);
   targetMap.get('item:driver').visible=false;targetMap.get('drop:driver').visible=false;
  }else if(['debrief','turn-on-light','complete'].includes(s.stage)){items.driver.visible=false;driver.visible=false;}
 }
 const armSolver=createTwoBoneSolver(.28,.29);
 const seatedArms=arms.map(()=>({shoulder:new T.Quaternion(),elbow:new T.Quaternion()}));
 const neutralArm=new T.Quaternion();
 function aimArm(arm,target){
  mentor.updateWorldMatrix(true,true);
  const shoulder=arm.shoulder.getWorldPosition(new T.Vector3()),solved=armSolver.solve(shoulder,target,new T.Vector3(0,0,1));
  if(!solved.reached)return false;
  const upper=solved.elbow.clone().sub(shoulder).transformDirection(mentor.matrixWorld.clone().invert());
  arm.shoulder.quaternion.setFromUnitVectors(new T.Vector3(0,-1,0),upper);
  arm.shoulder.updateWorldMatrix(true,true);
  const lower=solved.wrist.clone().sub(solved.elbow).transformDirection(arm.shoulder.matrixWorld.clone().invert());
  arm.elbow.quaternion.setFromUnitVectors(new T.Vector3(0,-1,0),lower);
  return true;
 }
 function seated(){
  if(mentor.parent!==van)van.add(mentor);mentor.position.set(-.52,0,-1.08);mentor.rotation.set(0,0,0);
  for(const leg of legs){leg.hip.rotation.x=Math.PI/2;leg.knee.rotation.x=-Math.PI/2;}
  van.updateWorldMatrix(true,true);wheel.updateWorldMatrix(true,false);
  for(let i=0;i<2;i++){
   aimArm(arms[i],wheel.localToWorld(new T.Vector3(i? .17:-.17,0,0)));
   seatedArms[i].shoulder.copy(arms[i].shoulder.quaternion);seatedArms[i].elbow.copy(arms[i].elbow.quaternion);
  }
 }
 function standing(){for(const leg of legs){leg.hip.rotation.x=0;leg.knee.rotation.x=0;}for(const arm of arms){arm.shoulder.rotation.set(0,0,0);arm.elbow.rotation.set(0,0,0);}}
 // Physical bench-to-board demonstration. Per part, in order: approach (walk,
 // then face the bench) -> reach -> grasp -> lift -> carry (walk, face the board)
 // -> place -> release -> retract. Only simulated dt advances it. The hand is
 // always the 0.29 m forearm tip solved by the two-bone IK; a part follows the
 // hand through a fixed grip point of its own geometry, and any out-of-reach
 // target rolls the phase back instead of dragging the part to the wrist.
 const DEMO_ORDER=['frame','differential','breaker-a'];
 const DEMO_SPEED=1.2,DEMO_TURN=2.2,DEMO_YAW=Math.PI/2,DEMO_REACH_EPS=1e-4;
 const DEMO_TIMES={reach:2.4,grasp:1,lift:2.2,place:3.4,release:.6,retract:1.8};
 const LANE_X=b.x+b.width/2+.4,PLACE_Z=c.backZ+c.depth+.32;
 // Standing as close as PLACE_Z puts the mounting point a few centimetres from her own
 // shoulder (frame), where the two-bone solution is nearly singular and the elbow flips.
 // Each part is therefore placed from the farthest stand (up to 0.28 m back) that keeps
 // the real arm within PLACE_COMFORT of the shoulder; never closer than PLACE_Z.
 const PLACE_COMFORT=.50,PLACE_BACK_MAX=.28,PLACE_BACK_STEP=.02;
 // Hand targets are in Camille's local frame (+x is her right side).
 const CARRY_HAND=new T.Vector3(.18,1.05,-.28),ARM_BEND=new T.Vector3(.5,-1,.35);
 const RETRACT_ASIDE=new T.Vector3(.36,1.0,-.10),HOVER=new T.Vector3(0,.08,.10),HOVER_TOP=new T.Vector3(0,.10,0),LIFT=new T.Vector3(0,.08,0);
 const LEAD_IN={frame:{x:0,z:.12},differential:{x:.04,z:.18},'breaker-a':{x:0,z:.18}};
 const partCache={},demo={done:new Set(),objPos:new T.Vector3(),objQuat:new T.Quaternion(),local:{p:new T.Vector3(),q:new T.Quaternion()},goal:new T.Vector3()};
 const IDENTITY=new T.Quaternion();
 function resetDemo(){
  demo.index=0;demo.phase='approach';demo.t=0;demo.route=null;demo.leg=0;demo.path=null;demo.hold=null;demo.turnDir=0;demo.stand=null;demo.liftTarget=null;
  demo.done.clear();demo.moving=false;demo.finished=false;demo.armBusy=false;demo.gait=0;demo.obstacles=[];demo.obstaclesFor=null;demo.liftDelay=0;
 }
 resetDemo();
 // Geometry-only measurements of the real item: local bounds, the grip point on
 // its outer surface and the exact authored mounted pose.
 // Closest point (world space) on any rendered triangle of `object` to `point`.
 function surfacePoint(object,point){
  const tri=new T.Triangle(),a=new T.Vector3(),bb=new T.Vector3(),cc=new T.Vector3(),near=new T.Vector3(),best=new T.Vector3();let bestDistance=Infinity;
  object.updateWorldMatrix(true,true);
  object.traverse(mesh=>{
   const pos=mesh.geometry?.attributes?.position;if(!mesh.isMesh||!pos)return;
   const index=mesh.geometry.index,n=index?index.count:pos.count;
   for(let i=0;i<n;i+=3){
    const at=k=>index?index.getX(i+k):i+k;
    a.fromBufferAttribute(pos,at(0)).applyMatrix4(mesh.matrixWorld);bb.fromBufferAttribute(pos,at(1)).applyMatrix4(mesh.matrixWorld);cc.fromBufferAttribute(pos,at(2)).applyMatrix4(mesh.matrixWorld);
    tri.set(a,bb,cc).closestPointToPoint(point,near);
    const dist=near.distanceTo(point);if(dist<bestDistance){bestDistance=dist;best.copy(near);}
   }
  });
  return best;
 }
 function partOf(id){
  if(partCache[id])return partCache[id];
  const o=items[id],keep={p:o.position.clone(),q:o.quaternion.clone(),m:o.userData.mounted,v:o.visible};
  resetItem(id);o.updateWorldMatrix(true,true);
  const box=new T.Box3().setFromObject(o,true).translate(o.position.clone().negate());
  const cx=(box.min.x+box.max.x)/2;
  // Candidates are snapped onto the closest rendered triangle, so the hand centre
  // touches real geometry rather than a bounding-box face. The grip is on the face
  // that points at Camille: the frame is always taken by its near (+z) rail; a
  // module is taken by its front (+z) face from the front of the bench and by its
  // back (-z) face from behind it.
  const snap=v=>surfacePoint(o,v.add(o.position)).sub(o.position);
  const grips={front:snap(id==='frame'?new T.Vector3(cx,box.max.y,box.max.z-.0125):new T.Vector3(cx,box.min.y+.155,box.max.z)),
   back:id==='frame'?null:snap(new T.Vector3(cx,box.min.y+.10,box.min.z))};
  // Rendered triangles in the part's own frame (root origin, no rotation).
  const tris=[],ta=new T.Vector3();
  o.traverse(mesh=>{
   const pos=mesh.geometry?.attributes?.position;if(!mesh.isMesh||!pos)return;
   const index=mesh.geometry.index,n=index?index.count:pos.count;
   for(let i=0;i<n;i++){ta.fromBufferAttribute(pos,index?index.getX(i):i).applyMatrix4(mesh.matrixWorld).sub(o.position);tris.push(ta.x,ta.y,ta.z);}
  });
  mountedPose(id);const finalPos=o.position.clone(),finalQuat=o.quaternion.clone();
  o.position.copy(keep.p);o.quaternion.copy(keep.q);o.userData.mounted=keep.m;o.visible=keep.v;
  const finalHands={};
  for(const side of ['front','back'])if(grips[side])finalHands[side]=grips[side].clone().applyQuaternion(finalQuat).add(finalPos);
  return partCache[id]={box,grips,finalPos,finalQuat,tris:Float32Array.from(tris),finalHands};
 }
 // Held-part clearance. The rendered triangles of the part, at a candidate pose,
 // must keep the trainee's cylinder (L.radius + 2 cm) clear and must not sink more
 // than 2 cm into any solid; the cabinet is exempt only around the part's own slot.
 const HELD_CLEAR=L.radius+.02,HELD_CONTACT=.02;
 const heldSolids=[
  ...L.solids.filter(q=>q.id!=='open-door').map(q=>({id:q.id,box:new T.Box3(new T.Vector3(q.minX,q.minY,q.minZ),new T.Vector3(q.maxX,q.minY+q.height,q.maxZ))})),
  // The toolbox stands on the bench and is not a layout solid.
  {id:'toolbox',box:new T.Box3(new T.Vector3(tb.x-tb.width/2,tb.y,tb.z-tb.depth/2),new T.Vector3(tb.x+tb.width/2,tb.y+tb.height,tb.z+tb.depth/2))},
 ].map(q=>({id:q.id,box:q.box.expandByScalar(-HELD_CONTACT)}));
 // Where Camille stands to take a part, chosen from the real arm: the right shoulder
 // sits 0.24 m to her right and must be within the 0.28+0.29 m arm of the grasp point
 // and of the hover point above it. Modules may be taken from the front (facing west)
 // or from behind the bench (facing east); the frame is always taken from the front.
 const REACH_LIMIT=.56,FRONT_Z=b.z+b.depth/2,BACK_Z=b.z-b.depth/2;
 function standSafe(x,z){for(let i=0;i<32;i++){const a=i*Math.PI/16;if(!canOccupy(x+.041*Math.cos(a),z+.041*Math.sin(a)))return false;}return true;}
 // Shoulder position when Camille stands at (x,z) facing yaw = +-90 degrees: it lies
 // 0.24 m north (west-facing) or south (east-facing) of her.
 const shoulderAt=(x,z,yaw)=>new T.Vector3(x+arms[1].shoulder.position.x*Math.cos(yaw),arms[1].shoulder.position.y,z-arms[1].shoulder.position.x*Math.sin(yaw));
 function standFor(id,part,source){
  const options=id==='frame'?[{side:'front',z:FRONT_Z+.35,yaw:DEMO_YAW}]:[{side:'front',z:FRONT_Z+.28,yaw:DEMO_YAW},{side:'back',z:BACK_Z-.29,yaw:-DEMO_YAW}];
  let best=null;
  for(const o0 of options){
   const grip=part.grips[o0.side],x=source.x+grip.x,grasp=source.clone().add(grip),hover=grasp.clone().add(id==='frame'?HOVER:HOVER_TOP);
   // Standing right above the part, the forearm hangs along its face and cuts into the
   // body (the shoulder is almost over it). Stand back, away from the bench, in 4 cm
   // steps until the whole reach (rest -> hover -> grasp) keeps the real arm meshes out
   // of the part and its neighbours; the arm limit tightens as the arm straightens.
   let o=o0,clear=false,shoulder=null;
   for(let back=0;back<=PICK_BACK_MAX+1e-9;back+=PICK_BACK_STEP){
    const c={...o0,x,z:o0.z+(o0.side==='front'?back:-back)},sh=shoulderAt(x,c.z,c.yaw);
    if(Math.max(sh.distanceTo(grasp),sh.distanceTo(hover))>(back?PICK_REACH_STRAIGHT:REACH_LIMIT)||!standSafe(x,c.z))continue;
    if(armSweepClear(c,source,grip,id)){o=c;clear=true;shoulder=sh;break;}
    if(!shoulder){o=c;shoulder=sh;}
   }
   if(!shoulder)continue;
   // The same hand must also reach the slot from the board stand, and its lead-in point.
   const end=part.finalHands[o.side],lead=LEAD_IN[id];
   const boardReach=pz=>{const board=shoulderAt(end.x,pz,DEMO_YAW);return Math.max(board.distanceTo(end),board.distanceTo(end.clone().add(new T.Vector3(lead.x,0,lead.z))));};
   let pz=PLACE_Z;
   for(let back=PLACE_BACK_MAX;back>PLACE_BACK_STEP/2;back-=PLACE_BACK_STEP){
    const z=PLACE_Z+back;
    if(boardReach(z)<=PLACE_COMFORT&&standSafe(end.x,z)){pz=z;break;}
   }
   const reach=Math.max(shoulder.distanceTo(grasp),shoulder.distanceTo(hover),boardReach(pz));
   if(reach<=REACH_LIMIT&&standSafe(x,o.z)&&(!best||(clear&&!best.clear)||(clear===best.clear&&reach<best.reach)))best={x,z:o.z,yaw:o.yaw,side:o.side,reach,pz,clear};
  }
  return best;
 }
 // ---- the real arm meshes against the parts left on the bench -------------------------
 const APPROACH_STANDOFF=.30,PICK_BACK_MAX=.40,PICK_BACK_STEP=.04,PICK_REACH_STRAIGHT=.53,REACH_SAMPLES=30;
 const armParts=[];arms[1].shoulder.traverse(m=>{if(m.isMesh)armParts.push(m);});
 // The hand is the finest mesh; its centre lies on the grasped surface by construction,
 // so it is exempt from the part being taken (never from the other parts).
 const handBall=armParts.reduce((a,m)=>!a||m.geometry.attributes.position.count>a.geometry.attributes.position.count?m:a,null);
 const armTri=new T.Triangle();
 function armHits(others,target){
  mentor.updateWorldMatrix(true,true);
  for(const m of armParts){
   const pos=m.geometry.attributes.position,index=m.geometry.index,n=index?index.count:pos.count;
   for(let i=0;i<n;i+=3){
    for(let k=0;k<3;k++)hv[k].fromBufferAttribute(pos,index?index.getX(i+k):i+k).applyMatrix4(m.matrixWorld);
    armTri.set(hv[0],hv[1],hv[2]);
    for(const b of others)if(b.intersectsTriangle(armTri))return true;
    if(m!==handBall&&target.intersectsTriangle(armTri))return true;
   }
  }
  return false;
 }
 // Poses Camille at `stand` for a moment (restored exactly) and sweeps the reach.
 function armSweepClear(stand,source,grip,id){
  items[id].updateWorldMatrix(true,true);
  const target=new T.Box3().setFromObject(items[id],true).expandByScalar(-BENCH_CONTACT);
  const arm=arms[1],keep={x:mentor.position.x,z:mentor.position.z,y:mentor.rotation.y,s:arm.shoulder.quaternion.clone(),e:arm.elbow.quaternion.clone()};
  mentor.position.x=stand.x;mentor.position.z=stand.z;mentor.rotation.y=stand.yaw;mentor.updateWorldMatrix(true,true);
  const grasp=source.clone().add(grip),hover=grasp.clone().add(id==='frame'?HOVER:HOVER_TOP);
  // The reach starts from the hanging arm, which still carries some walking swing
  // (+-0.32*0.7 rad about the shoulder, the largest the gait gives), so all three are swept.
  const shoulder=arm.shoulder.getWorldPosition(new T.Vector3()),point=new T.Vector3(),forward=new T.Vector3(Math.sin(stand.yaw),0,Math.cos(stand.yaw));
  let clear=true;
  for(const tilt of [0,.224,-.224]){
   const start=shoulder.clone().add(new T.Vector3(0,-.5699*Math.cos(tilt),0)).addScaledVector(forward,.5699*Math.sin(tilt));
   for(let i=0;i<=REACH_SAMPLES&&clear;i++){
    pathPoint([start,hover,grasp],i/REACH_SAMPLES,point);
    clear=holdHand(point)&&!armHits(demo.obstacles,target);
   }
  }
  mentor.position.x=keep.x;mentor.position.z=keep.z;mentor.rotation.y=keep.y;arm.shoulder.quaternion.copy(keep.s);arm.elbow.quaternion.copy(keep.e);
  mentor.updateWorldMatrix(true,true);
  return clear;
 }
 const hv=[new T.Vector3(),new T.Vector3(),new T.Vector3()],heldTri=new T.Triangle(),heldPos=new T.Vector3(),heldQuat=new T.Quaternion(),bodyQuat=new T.Quaternion(),Y_AXIS=new T.Vector3(0,1,0);
 function eachHeldTriangle(part,pos,quat,visit){
  for(let i=0;i<part.tris.length;i+=9){
   for(let k=0;k<3;k++)hv[k].set(part.tris[i+3*k],part.tris[i+3*k+1],part.tris[i+3*k+2]).applyQuaternion(quat).add(pos);
   if(visit(hv))return;
  }
 }
 function edgeDistance(px,pz,ax,az,bx,bz){
  const dx=bx-ax,dz=bz-az,l=dx*dx+dz*dz,u=l>0?Math.max(0,Math.min(1,((px-ax)*dx+(pz-az)*dz)/l)):0;
  return Math.hypot(px-ax-dx*u,pz-az-dz*u);
 }
 // Horizontal distance from the trainee's axis to the part (0 when inside a face).
 function heldPlayerGap(part,pos,quat,player){
  if(!player)return Infinity;
  let best=Infinity;const px=player.x,pz=player.z;
  eachHeldTriangle(part,pos,quat,([a,bb,cc])=>{
   const area=(bb.x-a.x)*(cc.z-a.z)-(cc.x-a.x)*(bb.z-a.z);
   if(Math.abs(area)>1e-12){
    const s1=(bb.x-a.x)*(pz-a.z)-(px-a.x)*(bb.z-a.z),s2=(cc.x-bb.x)*(pz-bb.z)-(px-bb.x)*(cc.z-bb.z),s3=(a.x-cc.x)*(pz-cc.z)-(px-cc.x)*(a.z-cc.z);
    if((s1>=0&&s2>=0&&s3>=0)||(s1<=0&&s2<=0&&s3<=0)){best=0;return true;}
   }
   best=Math.min(best,edgeDistance(px,pz,a.x,a.z,bb.x,bb.z),edgeDistance(px,pz,bb.x,bb.z,cc.x,cc.z),edgeDistance(px,pz,cc.x,cc.z,a.x,a.z));
  });
  return best;
 }
 function heldInSolid(id,part,pos,quat){
  const slot=slots[id],nearSlot=Math.hypot(pos.x-slot.x,pos.z-slot.z)<.35&&Math.abs(pos.y-slot.y)<.5;
  let hit=false;
  eachHeldTriangle(part,pos,quat,v=>{
   heldTri.set(v[0],v[1],v[2]);
   for(const q of heldSolids){if(q.id==='cabinet'&&nearSlot)continue;if(q.box.intersectsTriangle(heldTri)){hit=true;return true;}}
   for(const b of demo.obstacles){if(b.intersectsTriangle(heldTri)){hit=true;return true;}}
  });
  return hit;
 }
 // The other parts still standing on the bench (not the one being moved, none mounted):
 // the extent of their rendered triangles, 2 mm smaller so that legitimate neighbours a
 // few millimetres apart never count as touching. They never move during the demonstration.
 const BENCH_CONTACT=.002;
 function benchObstacles(id,placed){
  const boxes=[];
  for(const other of Object.keys(placed||{})){
   const o=items[other];
   if(other===id||!o||o.userData.mounted||demo.done.has(other))continue;
   o.updateWorldMatrix(true,true);
   boxes.push(new T.Box3().setFromObject(o,true).expandByScalar(-BENCH_CONTACT));
  }
  return boxes;
 }
 function heldHitsBench(part,pos,quat){
  let hit=false;
  eachHeldTriangle(part,pos,quat,v=>{
   heldTri.set(v[0],v[1],v[2]);
   for(const b of demo.obstacles){if(b.intersectsTriangle(heldTri)){hit=true;return true;}}
  });
  return hit;
 }
 // Pose of the carried part if Camille stood at (x,z) with heading yaw.
 function carriedPose(x,z,yaw){
  bodyQuat.setFromAxisAngle(Y_AXIS,yaw);
  heldQuat.copy(bodyQuat).multiply(demo.local.q);
  heldPos.copy(demo.local.p).applyQuaternion(bodyQuat);heldPos.x+=x;heldPos.z+=z;
 }
 // Gap at the candidate carried pose, or -1 when it is not allowed. A move that
 // already overlaps the trainee is only allowed if it increases the gap (retreat).
 function carriedGap(id,part,x,z,yaw,player,current){
  carriedPose(x,z,yaw);
  const gap=heldPlayerGap(part,heldPos,heldQuat,player);
  if(gap<HELD_CLEAR&&!(gap>current+1e-6))return -1;
  return heldInSolid(id,part,heldPos,heldQuat)?-1:gap;
 }
 function carriedGapNow(part,player){carriedPose(mentor.position.x,mentor.position.z,mentor.rotation.y);return heldPlayerGap(part,heldPos,heldQuat,player);}
 // Arm-driven candidate (lift / place): only the trainee is checked there; the bench
 // and slot contacts are the intended start and end of those motions.
 function armGapOk(id,part,pos,quat,player,current){
  // Not through the toolbox, furniture or any other part on the bench either (the cabinet
  // only around the part's own slot): heldInSolid covers the layout solids and the bench parts.
  if(heldInSolid(id,part,pos,quat))return false;
  const gap=heldPlayerGap(part,pos,quat,player);
  return gap>=HELD_CLEAR||gap>current+1e-6;
 }
 // Lift plan: the usual short rise while turning, unless the swinging part would cut into
 // another part on the bench or the arm could not follow; then rise vertically first
 // (higher and higher) and turn only afterwards. Checked on the rendered triangles and the
 // real arm along the whole lift; if no plan is clear the usual one is kept (and the
 // arm gate then holds the motion rather than letting the part pass through).
 const LIFT_RISES=[LIFT.y,.12,.16,.20,.24,.28,.32],LIFT_SAMPLES=240;
 const LIFT_ORDER=[...Array(LIFT_SAMPLES+1).keys()].filter(i=>i%4===0).concat([...Array(LIFT_SAMPLES+1).keys()].filter(i=>i%4!==0));
 // Where the part is carried (Camille's frame). The usual spot is beside and behind her;
 // standing right next to the toolbox it can lie inside it, so spots in front of her follow.
 const CARRY_HANDS=[CARRY_HAND,new T.Vector3(.18,1.05,.05),new T.Vector3(.18,1.05,.28),new T.Vector3(.18,1.2,.2),new T.Vector3(.12,1.15,.35)];
 function planLift(id,part,grip,hold,target){
  const sh=arms[1].shoulder.getWorldPosition(new T.Vector3()),bend=ARM_BEND.clone().transformDirection(mentor.matrixWorld);
  const goal=new T.Vector3(),pos=new T.Vector3(),q=new T.Quaternion();
  for(const spot of CARRY_HANDS){
   const carry=mentor.localToWorld(spot.clone());
   for(const rise of LIFT_RISES){
    const top=hold.clone().add(new T.Vector3(0,rise,0)),points=[hold,top,carry];
    const delay=rise===LIFT.y?0:rise/(rise+top.distanceTo(carry));
    let clear=true;
    // Every 4th sample first (cheap rejection), then all the others for the survivors.
    for(const i of LIFT_ORDER){
     if(!clear)break;
     const f=i/LIFT_SAMPLES;
     pathPoint(points,f,goal);
     q.slerpQuaternions(IDENTITY,target,Math.max(0,(f-delay)/(1-delay)));
     pos.copy(grip).applyQuaternion(q).negate().add(goal);
     clear=armSolver.solve(sh,goal,bend).error<=DEMO_REACH_EPS&&!heldInSolid(id,part,pos,q);
    }
    if(clear)return {rise,delay,carry};
   }
  }
  return {rise:LIFT.y,delay:0,carry:mentor.localToWorld(CARRY_HAND.clone())};
 }
 function handNow(out){mentor.updateWorldMatrix(true,true);return arms[1].elbow.localToWorld(out.set(0,-.29,0));}
 // Place Camille's right hand exactly on `target`; false (arm untouched) if out of reach.
 function holdHand(target){
  const arm=arms[1];mentor.updateWorldMatrix(true,true);
  const shoulder=arm.shoulder.getWorldPosition(new T.Vector3()),solved=armSolver.solve(shoulder,target,ARM_BEND.clone().transformDirection(mentor.matrixWorld));
  if(solved.error>DEMO_REACH_EPS)return false;
  const inverse=mentor.matrixWorld.clone().invert();
  const upper=solved.elbow.clone().sub(shoulder).transformDirection(inverse),axis=solved.wrist.clone().sub(shoulder).transformDirection(inverse);
  arm.shoulder.quaternion.copy(shoulderRotation(upper,axis));
  arm.shoulder.updateWorldMatrix(true,true);
  arm.elbow.quaternion.setFromUnitVectors(new T.Vector3(0,-1,0),solved.wrist.clone().sub(solved.elbow).transformDirection(arm.shoulder.matrixWorld.clone().invert()));
  arm.shoulder.updateWorldMatrix(true,true);
  return true;
 }
 // Orientation (Camille's frame) whose -Y is the upper arm and whose X is the normal of
 // the bend plane spanned by the shoulder-to-wrist axis and the preferred bend direction.
 const frameX=new T.Vector3(),frameZ=new T.Vector3(),frameB=new T.Vector3(),frameY=new T.Vector3(),frameM=new T.Matrix4();
 function shoulderFrame(upper,axis){
  const bend=ARM_BEND.clone().normalize();
  frameB.copy(bend).addScaledVector(axis,-bend.dot(axis));
  if(frameB.lengthSq()<1e-12)frameB.set(0,0,1).addScaledVector(axis,-axis.z);
  frameB.normalize();
  frameX.crossVectors(axis,frameB).normalize();
  frameY.copy(upper).negate();
  frameZ.crossVectors(frameX,frameY);
  return new T.Quaternion().setFromRotationMatrix(frameM.makeBasis(frameX,frameY,frameZ));
 }
 // The same frame for the arm hanging straight down, so that the rest pose is the identity.
 const SHOULDER_REST_INVERSE=shoulderFrame(new T.Vector3(0,-1,0),new T.Vector3(0,-1,0)).invert(),ARM_DOWN=new T.Vector3(0,-1,0);
 // Shoulder rotation taking -Y onto the upper arm. The shortest rotation (no twist) is
 // what the walking swing uses and is exact while the arm hangs or reaches forward, but it
 // is singular, and flips the whole arm, when the upper arm points straight up (modules
 // high on the board). From 69 degrees away from "down" the twist is therefore blended
 // towards the bend-plane frame, which is continuous there; from 126 degrees on it is
 // that frame alone.
 const TWIST_FROM=.15,TWIST_TO=2.4;
 function shoulderRotation(upper,axis){
  const theta=Math.acos(Math.max(-1,Math.min(1,-upper.y))),k=Math.max(0,Math.min(1,(theta-TWIST_FROM)/(TWIST_TO-TWIST_FROM))),w=k*k*(3-2*k);
  const frame=shoulderFrame(upper,axis).multiply(SHOULDER_REST_INVERSE);
  if(w>=1)return frame;
  const swing=new T.Quaternion().setFromUnitVectors(ARM_DOWN,upper);
  if(w<=0)return swing;
  const twist=swing.clone().invert().multiply(frame);
  if(twist.w<0){twist.x*=-1;twist.y*=-1;twist.z*=-1;twist.w*=-1;}
  return swing.multiply(new T.Quaternion().setFromAxisAngle(ARM_DOWN.clone().negate(),w*2*Math.atan2(twist.y,twist.w)));
 }
 // Point at fraction f of the polyline's length.
 function pathPoint(points,f,out){
  const lens=[];let total=0;
  for(let i=1;i<points.length;i++){const l=points[i].distanceTo(points[i-1]);lens.push(l);total+=l;}
  let rest=Math.max(0,Math.min(1,f))*total;
  for(let i=0;i<lens.length;i++){
   if(rest<=lens[i]||i===lens.length-1)return out.lerpVectors(points[i],points[i+1],lens[i]>0?Math.min(1,rest/lens[i]):1);
   rest-=lens[i];
  }
  return out.copy(points[0]);
 }
 // Points (excluding `from`, including `to`) of a hand path from `from` to `to` that turns
 // around `centre` (the shoulder): direction slerped, radius interpolated and never below
 // SWING_MIN_RADIUS, so the hand never sweeps through the joint. Chord fallback if opposed.
 const SWING_MIN_RADIUS=.25,SWING_STEPS=10;
 function swingAround(centre,from,to){
  const a=from.clone().sub(centre),b=to.clone().sub(centre),ra=a.length(),rb=b.length(),pts=[];
  const ua=a.clone().divideScalar(ra),ub=b.clone().divideScalar(rb),angle=Math.acos(Math.max(-1,Math.min(1,ua.dot(ub))));
  if(angle>Math.PI-.1)return [to.clone()];
  const sin=Math.sin(angle);
  for(let k=1;k<=SWING_STEPS;k++){
   const f=k/SWING_STEPS;
   const dir=sin>1e-6?ua.clone().multiplyScalar(Math.sin((1-f)*angle)/sin).addScaledVector(ub,Math.sin(f*angle)/sin):ub.clone();
   const r=Math.max(ra+(rb-ra)*f,Math.min(SWING_MIN_RADIUS,ra,rb));
   pts.push(dir.multiplyScalar(r).add(centre));
  }
  pts[pts.length-1].copy(to);
  return pts;
 }
 // `allow(yaw)` lets a carried part veto a turn that would swing it into the trainee.
 const TURN_PLAN_STEP=.04;
 function turnTo(target,dt,allow=null){
  let diff=target-mentor.rotation.y;diff-=2*Math.PI*Math.floor((diff+Math.PI)/(2*Math.PI));
  // A half turn always goes the same way (via the side away from the board).
  if(diff>Math.PI-1e-6)diff-=2*Math.PI;
  if(Math.abs(diff)<1e-9){mentor.rotation.y=target;demo.turnDir=0;return true;}
  if(dt<=0)return false;
  // A turn already under way keeps its direction (even the long way round).
  if(demo.turnDir&&Math.sign(diff)!==demo.turnDir)diff+=demo.turnDir*2*Math.PI;
  const attempt=d=>{const turn=Math.min(Math.abs(d),DEMO_TURN*dt);return {turn,yaw:mentor.rotation.y+Math.sign(d)*turn,whole:turn>=Math.abs(d)};};
  demo.turnBlocked=false;
  if(allow&&!demo.turnDir){
   // Plan a new turn: sweep the carried part through the whole turn each way (the short
   // way first) and take the first way that stays clear, whatever the angle. A veto of
   // the short way alone used to be tried the other way only for turns of 90 degrees or more.
   const swept=d=>{for(let a=TURN_PLAN_STEP;;a+=TURN_PLAN_STEP){const turn=Math.min(a,Math.abs(d));if(!allow(mentor.rotation.y+Math.sign(d)*turn))return false;if(turn>=Math.abs(d))return true;}};
   const other=diff-Math.sign(diff)*2*Math.PI;
   if(swept(diff)){/* short way is clear */}
   else if(swept(other))diff=other;
   else{demo.turnBlocked=true;return false;}
  }
  let go=attempt(diff);
  if(allow&&!allow(go.yaw)){demo.turnBlocked=true;return false;}
  demo.turnDir=Math.sign(diff);
  mentor.rotation.y=go.yaw;
  if(go.whole){demo.turnDir=0;}
  return go.whole;
 }
 // While carrying, the part's footprint must stay clear for every translation and turn.
 function carryGuard(player){
  if(demo.phase!=='carry')return null;
  const id=DEMO_ORDER[demo.index],part=partOf(id);
  return yaw=>carriedGap(id,part,mentor.position.x,mentor.position.z,yaw,player,carriedGapNow(part,player))>=0;
 }
 // One bounded walking step toward `goal`: same trainee and furniture clearance
 // as the other walking stages. Returns arrived | moving | blocked | idle.
 function walkLeg(goal,dt,player,out){
  const dx=goal.x-mentor.position.x,dz=goal.z-mentor.position.z,dist=Math.hypot(dx,dz);
  if(dist<1e-7)return 'arrived';
  if(dt<=0)return 'idle';
  const ux=dx/dist,uz=dz/dist;let step=Math.min(dist,dt*DEMO_SPEED);
  if(demo.phase==='carry'){
   // With a part in hand, a large heading change is made on the spot (the swept
   // volume is checked at every step) rather than while walking sideways.
   const heading=Math.atan2(-dx,-dz);
   let diff=heading-mentor.rotation.y;diff-=2*Math.PI*Math.floor((diff+Math.PI)/(2*Math.PI));
   if(Math.abs(diff)>.6){turnTo(heading,dt,carryGuard(player));if(demo.turnBlocked)out.waiting=true;return 'turning';}
   demo.turnDir=0;
  }
  if(player){
   const ox=mentor.position.x-player.x,oz=mentor.position.z-player.z,gap=L.radius+.27;
   const along=ox*ux+oz*uz,outside=ox*ox+oz*oz-gap*gap,disc=along*along-outside;
   if(outside<0)step=0;else if(along<0&&disc>=0)step=Math.min(step,Math.max(0,-along-Math.sqrt(disc)));
  }
  const count=Math.ceil(step/.02),increment=count?step/count:0;let moved=false;
  const carrying=demo.phase==='carry'?{id:DEMO_ORDER[demo.index],part:partOf(DEMO_ORDER[demo.index])}:null;
  let gap=carrying?carriedGapNow(carrying.part,player):Infinity;
  for(let n=0;n<count;n++){
   const x=mentor.position.x+ux*increment,z=mentor.position.z+uz*increment;
   let safe=true;for(let i=0;i<32;i++){const a=i*Math.PI/16;if(!canOccupy(x+.041*Math.cos(a),z+.041*Math.sin(a))){safe=false;break;}}
   if(safe&&carrying){gap=carriedGap(carrying.id,carrying.part,x,z,mentor.rotation.y,player,gap);safe=gap>=0;}
   if(!safe){out.waiting=true;break;}
   mentor.position.x=x;mentor.position.z=z;moved=true;
  }
  if(step<1e-8)out.waiting=true;
  if(moved){out.moving=true;turnTo(Math.atan2(-dx,-dz),dt,carryGuard(player));}
  return moved?'moving':'blocked';
 }
 function followRoute(dt,player,out){
  while(demo.leg<demo.route.length){
   if(walkLeg(demo.route[demo.leg],dt,player,out)!=='arrived')return false;
   demo.leg++;
  }
  return true;
 }
 function demoGait(moving,dt){
  if(dt>0){demo.gait=T.MathUtils.clamp(demo.gait+(moving&&!reducedMotion?1:-1)*dt*6,0,1);if(moving&&!reducedMotion)walkClock+=dt*7;}
  for(let i=0;i<2;i++){
   const swing=Math.sin(walkClock+i*Math.PI)*.32*demo.gait;
   legs[i].hip.rotation.x=swing;legs[i].knee.rotation.x=Math.max(0,-swing)*.5;
   if(i===0||!demo.armBusy){arms[i].shoulder.rotation.set(-swing*.7,0,0);arms[i].elbow.rotation.set(0,0,0);}
  }
 }
 function setObject(hand,quat,grip){demo.objQuat.copy(quat);demo.objPos.copy(grip).applyQuaternion(quat).negate().add(hand);demo.moving=true;}
 function demoStep(s,dt,player){
  const out={moving:false,waiting:false,finished:demo.finished};
  if(demo.finished)return out;
  demo.turnBlocked=false;
  const id=DEMO_ORDER[demo.index],drop=s.placed?.[id];
  if(!drop){out.waiting=true;return out;}
  if(demo.obstaclesFor!==id){demo.obstacles=benchObstacles(id,s.placed);demo.obstaclesFor=id;}
  const part=partOf(id),side=demo.stand?.side??'front',grip=part.grips[side],source=new T.Vector3(drop.x,drop.y,drop.z);
  const finalHand=part.finalHands[side],placeX=finalHand.x;
  mentor.updateWorldMatrix(true,true);
  const tick=dur=>{demo.t=Math.min(dur,demo.t+dt);return demo.t>=dur;};
  const eased=dur=>{const u=demo.t/dur;return u*u*(3-2*u);};
  // Advance a timed hand motion; null = target unreachable, time rolled back.
  const drive=(dur,points,gate=null)=>{
   const before=demo.t,done=tick(dur);
   pathPoint(points,eased(dur),demo.goal);
   if((gate&&!gate(demo.goal))||!holdHand(demo.goal)){demo.t=before;out.waiting=true;return null;}
   return done;
  };
  // Gate for the arm-driven phases: the part's candidate pose must keep the trainee clear.
  const armGate=(quatAt)=>goal=>{
   const q=quatAt(),pos=grip.clone().applyQuaternion(q).negate().add(goal);
   const current=demo.moving?heldPlayerGap(part,demo.objPos,demo.objQuat,player):heldPlayerGap(part,source,IDENTITY,player);
   return armGapOk(id,part,pos,q,player,current);
  };
  const next=phase=>{demo.phase=phase;demo.t=0;demo.path=null;};
  switch(demo.phase){
   case 'approach':{
    if(!demo.route){
     const stand=standFor(id,part,source);
     // No stand within the real arm's reach: stay put rather than stretch the arm.
     if(!stand){out.waiting=true;break;}
     demo.stand=stand;
     // Walk along the bench 0.30 m farther out than the stand, then step in at the stand's
     // x: her hanging arm is then never swept along parts standing at the bench edge.
     const away=stand.side==='front'?APPROACH_STANDOFF:-APPROACH_STANDOFF;
     const lane={x:LANE_X,z:stand.z+away},mid={x:stand.x,z:stand.z+away},spot={x:stand.x,z:stand.z};
     demo.route=demo.index===0?[lane,mid,spot]:[{x:LANE_X,z:PLACE_Z},lane,mid,spot];
     demo.leg=0;
    }
    if(followRoute(dt,player,out)&&turnTo(demo.stand.yaw,dt)){demo.route=null;next('reach');}
    break;
   }
   case 'reach':{
    if(!demo.path){
     const grasp=source.clone().add(grip);
     demo.path=[handNow(new T.Vector3()),grasp.clone().add(id==='frame'?HOVER:HOVER_TOP),grasp];demo.hold=grasp;demo.armBusy=true;
    }
    if(drive(DEMO_TIMES.reach,demo.path))next('grasp');
    break;
   }
   case 'grasp':{
    if(drive(DEMO_TIMES.grasp,[demo.hold,demo.hold])===true)next('lift');
    break;
   }
   case 'lift':{
    if(!demo.path){
     // The part must arrive at the board with its mounted orientation once Camille
     // faces the board (DEMO_YAW), whichever side of the bench she lifted it from.
     demo.liftTarget=new T.Quaternion().setFromAxisAngle(Y_AXIS,mentor.rotation.y-DEMO_YAW).multiply(part.finalQuat);
     const plan=planLift(id,part,grip,demo.hold,demo.liftTarget);
     demo.path=[demo.hold.clone(),demo.hold.clone().add(new T.Vector3(0,plan.rise,0)),plan.carry];demo.liftDelay=plan.delay;
    }
    // The part turns only once the lift has brought it up by `delay` of the way.
    const liftQuat=()=>{
     const f=eased(DEMO_TIMES.lift);
     return new T.Quaternion().slerpQuaternions(IDENTITY,demo.liftTarget,demo.liftDelay>=1?0:Math.max(0,(f-demo.liftDelay)/(1-demo.liftDelay)));
    };
    const r=drive(DEMO_TIMES.lift,demo.path,armGate(liftQuat));
    if(r===null)break;
    const q=liftQuat();
    setObject(demo.goal,q,grip);
    if(r){
     // From here the part is rigid in Camille's frame, whatever her heading.
     const inverse=mentor.matrixWorld.clone().invert();
     demo.local.p.copy(demo.objPos).applyMatrix4(inverse);demo.local.q.copy(mentor.quaternion).invert().multiply(demo.objQuat);
     next('carry');demo.route=null;
    }
    break;
   }
   case 'carry':{
    if(!demo.route){demo.route=[{x:LANE_X,z:mentor.position.z},{x:LANE_X,z:demo.stand.pz},{x:placeX,z:demo.stand.pz}];demo.leg=0;}
    const arrived=followRoute(dt,player,out)&&turnTo(DEMO_YAW,dt,carryGuard(player));
    if(demo.turnBlocked)out.waiting=true;
    mentor.updateWorldMatrix(true,true);
    demo.objPos.copy(demo.local.p).applyMatrix4(mentor.matrixWorld);demo.objQuat.copy(mentor.quaternion).multiply(demo.local.q);
    holdHand(demo.goal.copy(grip).applyQuaternion(demo.objQuat).add(demo.objPos));
    if(arrived){demo.route=null;next('place');}
    break;
   }
   case 'place':{
    if(!demo.path){
     const end=finalHand,lead=LEAD_IN[id],start=demo.goal.copy(grip).applyQuaternion(demo.objQuat).add(demo.objPos).clone();
     const pre=end.clone().add(new T.Vector3(lead.x,0,lead.z));
     // Slide in through the frame window: in front of it, then straight in.
     const sh=arms[1].shoulder.getWorldPosition(new T.Vector3());
     demo.path=[start,...swingAround(sh,start,pre),...(lead.x?[end.clone().add(new T.Vector3(lead.x,0,0)),end.clone()]:[end.clone()])];
     demo.hold=end.clone();
    }
    const r=drive(DEMO_TIMES.place,demo.path,armGate(()=>part.finalQuat));
    if(r===null)break;
    setObject(demo.goal,part.finalQuat,grip);
    if(r){demo.done.add(id);demo.moving=false;next('release');}
    break;
   }
   case 'release':{
    if(drive(DEMO_TIMES.release,[demo.hold,demo.hold])===true)next('retract');
    break;
   }
   case 'retract':{
    if(!demo.path){
     const lead=LEAD_IN[id],back=demo.hold.clone().add(new T.Vector3(0,0,lead.z));
     const rest=arms[1].shoulder.getWorldPosition(new T.Vector3()).add(new T.Vector3(0,-.5699,0));
     // Swing out beside the shoulder: a straight line to rest would cross it.
     const aside=mentor.localToWorld(RETRACT_ASIDE.clone());
     // A straight segment can pass a few centimetres from the shoulder joint, where the
     // elbow flips; both legs therefore swing around the shoulder at a safe radius.
     const sh=arms[1].shoulder.getWorldPosition(new T.Vector3());
     demo.path=[demo.hold.clone(),back,...swingAround(sh,back,aside),...swingAround(sh,aside,rest)];
    }
    if(drive(DEMO_TIMES.retract,demo.path)===true){
     arms[1].shoulder.rotation.set(0,0,0);arms[1].elbow.rotation.set(0,0,0);demo.armBusy=false;
     demo.index++;demo.route=null;demo.stand=null;
     if(demo.index>=DEMO_ORDER.length)demo.finished=true;else next('approach');
    }
    break;
   }
  }
  out.finished=demo.finished;
  return out;
 }
 // After sync() has reset every part to its stored bench point: mounted parts
 // sit on their slots and the part in Camille's hand follows the solved pose.
 function applyDemoObjects(){
  for(const id of demo.done)mountedPose(id);
  if(demo.moving&&demo.index<DEMO_ORDER.length){
   const o=items[DEMO_ORDER[demo.index]];o.position.copy(demo.objPos);o.quaternion.copy(demo.objQuat);o.visible=true;
  }
 }
 function update(s,dt,player,options={}){
  dt=Number.isFinite(dt)?Math.max(0,Math.min(dt,.1)):0;
  reducedMotion=!!options.reducedMotion;
  usingTool=!!options.usingTool&&s.stage==='fasten'&&s.held==='driver';
  const stageChanged=s.stage!==lastStage;
  if(stageChanged&&s.stage==='follow'){waypoint=0;walkClock=0;}
  if(stageChanged&&s.stage==='demo'){resetDemo();walkClock=0;}
  if(stageChanged&&s.stage==='drive'){doorAmount=0;door.rotation.y=0;passengerDoor.rotation.y=0;}
  const drive=RIDING.has(s.stage),p=poseVan(s);
  van.position.set(p.x,0,p.z);van.rotation.y=p.yaw;
  const distance=Number.isFinite(s.driveDistance)?Math.max(0,s.driveDistance):0;
  const cameraZ=CINEMATIC.has(s.stage)?p.z:(player?.z??L.spawn.z);
  updateRoadside(distance,cameraZ);
  cullPoint.set(p.x,L.eyeHeight,cameraZ);scenery.visible=siteBounds.distanceToPoint(cullPoint)<=scene.fog.far;
  let mentorWaiting=false,followReached=false,boardReached=false,moving=false,demoFinished=false;
  if(drive){seated();doorAmount=0;door.rotation.y=0;passengerDoor.rotation.y=driverDoor.rotation.y=0;}
  else if(OUTSIDE_BRIEF.has(s.stage)){
   const time=s.stage==='exit'?Math.max(0,s.time||0):DURATIONS.exit;
   const u=T.MathUtils.smoothstep(time,.7,DURATIONS.exit),opening=T.MathUtils.smoothstep(time,0,.7);
   passengerDoor.rotation.y=1.35*opening;driverDoor.rotation.y=-1.35*opening;
   if(mentor.parent!==scene)scene.add(mentor);
   mentor.position.set(L.van.park.x+L.van.seat.z,0,T.MathUtils.lerp(L.van.park.z+.52,L.mentor.exit.z,u));
   mentor.rotation.set(0,T.MathUtils.lerp(L.van.park.yaw,0,u),0);
   for(const leg of legs){leg.hip.rotation.x=Math.PI/2*(1-u);leg.knee.rotation.x=-Math.PI/2*(1-u);}
   for(let i=0;i<arms.length;i++){
    arms[i].shoulder.quaternion.slerpQuaternions(seatedArms[i].shoulder,neutralArm,u);
    arms[i].elbow.quaternion.slerpQuaternions(seatedArms[i].elbow,neutralArm,u);
   }
   doorAmount=0;door.rotation.y=0;
  }
  else if(s.stage==='follow'){
   passengerDoor.rotation.y=1.35;driverDoor.rotation.y=-1.35;
   if(mentor.parent!==scene){scene.add(mentor);mentor.position.set(L.mentor.exit.x,0,L.mentor.exit.z);mentor.rotation.set(0,0,0);}
   const goal=L.mentor.waypoints[waypoint];
   const playerDistance=player?Math.hypot(player.x-mentor.position.x,player.z-mentor.position.z):Infinity;
   const destination=L.mentor.waypoints[L.mentor.waypoints.length-1];
   const traineeBehind=player&&Math.hypot(player.x-destination.x,player.z-destination.z)>Math.hypot(mentor.position.x-destination.x,mentor.position.z-destination.z)+.4;
   mentorWaiting=playerDistance>L.mentor.waitDistance&&Boolean(traineeBehind);
   doorAmount=s.houseDoorOpen?Math.min(1,doorAmount+dt*1.2):0;
   // The trainee owns the latch. Camille waits outside until the rendered leaf
   // clears the passage; an interaction is never implied by walking or time.
   if(goal&&goal.z<d.z&&mentor.position.z>=d.z&&doorAmount<1)mentorWaiting=true;
   {
    mentor.position.y=0;
    if(goal&&!mentorWaiting){
     const dx=goal.x-mentor.position.x,dz=goal.z-mentor.position.z,dist=Math.hypot(dx,dz);
     if(dist<.045){waypoint++;}
     else{
      let step=Math.min(dist,dt*1.2);const ux=dx/dist,uz=dz/dist;
      if(player){
       // Sweep only Camille's next step against the same footprint as player
       // movement. Wait at contact; never push the trainee or block retreat.
       const ox=mentor.position.x-player.x,oz=mentor.position.z-player.z,gap=L.radius+.27;
       const along=ox*ux+oz*uz,outside=ox*ox+oz*oz-gap*gap,disc=along*along-outside;
       if(along<0&&disc>0)step=Math.min(step,Math.max(0,-along-Math.sqrt(disc)));
      }
      if(step<1e-8)mentorWaiting=true;
      else{mentor.position.x+=ux*step;mentor.position.z+=uz*step;mentor.rotation.y=Math.atan2(-dx,-dz);moving=true;}
     }
    }
   }
   door.rotation.y=d.openAngle*doorAmount;
   followReached=!!s.houseDoorOpen&&waypoint>=L.mentor.waypoints.length&&!mentorWaiting&&playerDistance<2.4;
   if(moving){walkClock+=dt*7;for(let i=0;i<2;i++){const swing=Math.sin(walkClock+i*Math.PI)*.32;legs[i].hip.rotation.x=swing;legs[i].knee.rotation.x=Math.max(0,-swing)*.5;arms[i].shoulder.rotation.x=-swing*.7;}}
   else standing();
   if(mentorWaiting&&player)mentor.rotation.y=Math.atan2(mentor.position.x-player.x,mentor.position.z-player.z);
  }else if(s.stage==='walk-to-board'){
   if(mentor.parent!==scene)scene.attach(mentor);
   doorAmount=1;door.rotation.y=d.openAngle;passengerDoor.rotation.y=1.35;
   const goal=L.mentor.board,dx=goal.x-mentor.position.x,dz=goal.z-mentor.position.z,dist=Math.hypot(dx,dz);
   const playerDistance=player?Math.hypot(player.x-mentor.position.x,player.z-mentor.position.z):Infinity;
   mentorWaiting=playerDistance>L.mentor.waitDistance;
   if(dt>0&&dist>1e-8&&!mentorWaiting){
    const ux=dx/dist,uz=dz/dist,gap=L.radius+.27;
    let step=Math.min(dist,dt*1.2);
    if(player){
     const ox=mentor.position.x-player.x,oz=mentor.position.z-player.z;
     const along=ox*ux+oz*uz,outside=ox*ox+oz*oz-gap*gap,disc=along*along-outside;
     if(outside<0)step=0;
     else if(along<0&&disc>=0)step=Math.min(step,Math.max(0,-along-Math.sqrt(disc)));
    }
    // Sweep a 27 cm disc against the existing 23 cm navigation collider.
    // A slightly inflated ring covers the gaps between samples.
    const safe=(x,z)=>{for(let i=0;i<32;i++){const a=i*Math.PI/16;if(!canOccupy(x+.041*Math.cos(a),z+.041*Math.sin(a)))return false;}return true;};
    const count=Math.ceil(step/.02),increment=count?step/count:0;
    for(let i=0;i<count;i++){
     const x=mentor.position.x+ux*increment,z=mentor.position.z+uz*increment;
     if(!safe(x,z)){mentorWaiting=true;break;}
     mentor.position.x=x;mentor.position.z=z;moving=true;
    }
    if(step<1e-8)mentorWaiting=true;
    if(moving&&(!player||Math.hypot(player.x-mentor.position.x,player.z-mentor.position.z)>L.radius+.37))mentor.rotation.y=Math.atan2(-dx,-dz);
   }
   if(moving&&!reducedMotion){walkClock+=dt*7;for(let i=0;i<2;i++){const swing=Math.sin(walkClock+i*Math.PI)*.32;legs[i].hip.rotation.x=swing;legs[i].knee.rotation.x=Math.max(0,-swing)*.5;arms[i].shoulder.rotation.x=-swing*.7;}}
   else standing();
   boardReached=dist<1e-6&&playerDistance<2.4;
  }else if(s.stage==='demo'){
   if(mentor.parent!==scene)scene.attach(mentor);
   doorAmount=1;door.rotation.y=d.openAngle;passengerDoor.rotation.y=1.35;
   const step=demoStep(s,dt,player);
   moving=step.moving;mentorWaiting=step.waiting;demoFinished=step.finished;
   demoGait(moving,dt);
  }else{
   if(mentor.parent!==scene)scene.add(mentor);standing();doorAmount=1;door.rotation.y=d.openAngle;passengerDoor.rotation.y=1.35;
   // The existing follow arrival has a small authored alignment; subsequent
   // kit/dialogue frames must not overwrite Camille's actual position.
   if((s.stage==='toolbox'&&stageChanged)||(!lastStage&&['collect','ready'].includes(s.stage))){
    const wait=L.mentor.waypoints[L.mentor.waypoints.length-1];mentor.position.set(wait.x,0,wait.z);mentor.rotation.y=.4;
   }

  }
  setDoorAngle(door.rotation.y);
  // State snapshots are immutable; avoid rebuilding all proxies every frame.
  if(!lastState||s.stage!==lastState.stage||s.houseDoorOpen!==lastState.houseDoorOpen||s.held!==lastState.held||s.installed!==lastState.installed||s.placed!==lastState.placed||usingTool!==lastUsingTool||s.screw!==lastState.screw||s.stage==='demo')sync(s);
  if(s.stage==='demo')applyDemoObjects();
  mentor.updateWorldMatrix(true,true);mentor.getWorldPosition(temp);targetMap.get('mentor').position.set(temp.x,temp.y+.87,temp.z);
  for(const o of teleports)o.visible=!!options.immersive;
  lastStage=s.stage;lastUsingTool=usingTool;
  // Completion is reported only by the demonstration state machine, after the
  // third part is mounted and Camille's arm has returned to rest; never by time.
  return {animating:TRAVEL_ANIMATION.has(s.stage)||s.stage==='walk-to-board'||(s.stage==='follow'&&(moving||!mentorWaiting&&waypoint<L.mentor.waypoints.length||doorAmount>0&&doorAmount<1))||s.stage==='demo',mentorWaiting,followReached,boardReached,demoDone:s.stage==='demo'&&demoFinished};
 }
 function findPlacement(ray,item,{benchOnly=false}={}){
  const q=L.itemDimensions[item];if(!q||!ray?.origin||!ray?.direction)return null;
  const dir=ray.direction,origin=ray.origin;if(![dir.x,dir.y,dir.z,origin.x,origin.y,origin.z].every(Number.isFinite)||dir.y>=-1e-6)return null;
  const surfaces=[{y:b.top,minX:b.x-b.width/2,maxX:b.x+b.width/2,minZ:b.z-b.depth/2,maxZ:b.z+b.depth/2},...(benchOnly?[]:[{y:0,...L.bounds}])];
  for(const sf of surfaces){
   const t=(sf.y-origin.y)/dir.y;if(t<0)continue;
   const x=origin.x+dir.x*t,z=origin.z+dir.z*t,hw=q.width/2+.006,hd=q.depth/2+.006;
   if(x-hw<sf.minX||x+hw>sf.maxX||z-hd<sf.minZ||z+hd>sf.maxZ)continue;
   const overlap=s=>x+hw>s.minX&&x-hw<s.maxX&&z+hd>s.minZ&&z-hd<s.maxZ;
   // If the ray meets the bench or any solid, do not fall through to the floor.
   if(L.solids.some(s=>(sf.y===b.top?s.id!=='bench':true)&&s.minY<sf.y+q.height&&s.minY+s.height>sf.y+1e-5&&overlap(s)))return null;
   if(sf.y===b.top&&x+hw>tb.x-tb.width/2&&x-hw<tb.x+tb.width/2&&z+hd>tb.z-tb.depth/2&&z-hd<tb.z+tb.depth/2)return null;
   if(!lineClear(origin,{x,y:sf.y+.003,z}))return null;
   const occupied=Object.entries(lastState?.placed||{}).some(([id,p])=>{if(id===item)return false;const dim=L.itemDimensions[id];return dim&&Math.abs(p.y-sf.y)<.01&&Math.abs(p.x-x)<(dim.width+q.width)/2+.008&&Math.abs(p.z-z)<(dim.depth+q.depth)/2+.008;});
   mentor.getWorldPosition(temp);const mx=Math.max(0,Math.abs(temp.x-x)-hw),mz=Math.max(0,Math.abs(temp.z-z)-hd);
   if(occupied||sf.y<1.5&&Math.hypot(mx,mz)<.28)return null;
   return {x,y:sf.y,z};
  }
  return null;
 }
 setDoorAngle(0);
 sync({stage:'drive',time:0,held:null,installed:[],placed:{},screw:0});seated();const initial=poseVan({stage:'drive',time:0});van.position.set(initial.x,0,initial.z);
 return {scene,site:scenery,roadside,roadsideBatches,sceneryStats,handView,targets,teleports,panelMount,mentor,van,poster,focusRing,door,passengerDoor,driverDoor,toolbox,lid,frontFlap,items,driver,screw,cover,materials,slots,update,cameraPose,spawn,targetPosition,findPlacement,sync,pickTarget,caseSurfaceHit};
}
